import { setDetailsForReceiptPrint } from "./ReceiptHelper";
import EscPosEncoder from "./EscPosEncoder";
import { getNepaliDateBS, getNepaliFiscalYear, formatReceiptDateTime, numberToWords } from "./BillFormatsHelper";

// Module-level reference to the connected Web Bluetooth device
let connectedBluetoothDevice = null;
let bluetoothCharacteristic = null;

// Session-based bill counter: starts at 1 when the system starts, increments with each printed bill
let __billCounter = 0;
export const getNextBillNumber = () => ++__billCounter;
export const getCurrentBillNumber = () => __billCounter;
export const resetBillCounter = () => { __billCounter = 0; };

export const setBluetoothDevice = (device, characteristic) => {
  connectedBluetoothDevice = device;
  bluetoothCharacteristic = characteristic;
};

export const getBluetoothDevice = () => {
  return connectedBluetoothDevice;
};

// Print via Web Bluetooth (ESC/POS)
export const printViaWebBluetooth = async (html, text) => {
  if (!bluetoothCharacteristic) {
    throw new Error("No Bluetooth printer connected");
  }

  try {
    const encoder = new EscPosEncoder();
    encoder.initialize();
    
    // Split the text into lines and print
    const cleanText = (text || '').replace(/<\/?b>|<\/?big>|<\/?center>/g, '');
    const lines = cleanText.split('\n');
    lines.forEach(line => {
      encoder.line(line);
    });
    
    encoder.cut();
    
    const buffer = encoder.encode();
    
    // Bluetooth characteristic value is limited in size (usually 512 bytes)
    // We need to chunk the data
    const CHUNK_SIZE = 300;
    for (let i = 0; i < buffer.length; i += CHUNK_SIZE) {
      const chunk = buffer.slice(i, i + CHUNK_SIZE);
      await bluetoothCharacteristic.writeValue(chunk);
    }
    return "Printed successfully via Bluetooth";
  } catch (error) {
    console.error("Bluetooth printing failed:", error);
    throw error;
  }
};

// Direct silent printing via local WebSocket agent
export const printViaWebSocketAgent = (printerName, html, text, pageFormat = '80') => {
  return new Promise((resolve, reject) => {
    let settled = false;
    const wsUrl = localStorage.getItem("printer_agent_url") || "ws://127.0.0.1:8124";
    const resolvedPrinter = printerName || localStorage.getItem("billPrinter") || "Default";
    let ws = null;

    const timeout = setTimeout(() => {
      if (!settled) {
        settled = true;
        try { if (ws) ws.close(); } catch(e){}
        reject(new Error("Print agent response timed out"));
      }
    }, 8500);

    try {
      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        try {
          ws.send(JSON.stringify({
            printerName: resolvedPrinter,
            html,
            text,
            pageFormat
          }));
        } catch (sendErr) {
          if (!settled) {
            settled = true;
            clearTimeout(timeout);
            reject(sendErr);
          }
        }
      };

      ws.onmessage = (event) => {
        if (settled) return;
        try {
          const response = JSON.parse(event.data);
          if (response.type === 'print_response') {
            settled = true;
            clearTimeout(timeout);
            try { ws.close(); } catch(e){}
            if (response.success) {
              resolve(response.message);
            } else {
              reject(new Error(response.message));
            }
          }
        } catch (err) {
          if (!settled) {
            settled = true;
            clearTimeout(timeout);
            try { ws.close(); } catch(e){}
            reject(err);
          }
        }
      };

      ws.onerror = (err) => {
        if (!settled) {
          settled = true;
          clearTimeout(timeout);
          try { ws.close(); } catch(e){}
          reject(new Error("Cannot connect to local print agent on " + wsUrl));
        }
      };
    } catch (e) {
      if (!settled) {
        settled = true;
        clearTimeout(timeout);
        reject(e);
      }
    }
  });
};

// Cached Discovered OS Printers Local Storage helpers
export const getCachedPrinters = () => {
  try {
    const raw = localStorage.getItem('discovered_os_printers');
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
};

export const saveCachedPrinters = (printersList) => {
  try {
    if (Array.isArray(printersList)) {
      const existing = getCachedPrinters();
      const manuals = existing.filter(p => p.isManual);
      const merged = [...printersList];
      manuals.forEach(m => {
        if (!merged.some(p => p.name.toLowerCase() === m.name.toLowerCase())) {
          merged.push(m);
        }
      });
      localStorage.setItem('discovered_os_printers', JSON.stringify(merged));
      return merged;
    }
  } catch (e) {}
  return printersList;
};

export const addCustomOsPrinter = (printerName) => {
  const clean = (printerName || '').trim();
  if (!clean) return getCachedPrinters();
  const current = getCachedPrinters();
  if (!current.some(p => p.name.toLowerCase() === clean.toLowerCase())) {
    current.push({
      name: clean,
      displayName: clean,
      isDefault: current.length === 0,
      isManual: true,
      status: 0
    });
    localStorage.setItem('discovered_os_printers', JSON.stringify(current));
  }
  return current;
};

export const removeCustomOsPrinter = (printerName) => {
  const clean = (printerName || '').trim();
  const current = getCachedPrinters().filter(p => p.name.toLowerCase() !== clean.toLowerCase());
  localStorage.setItem('discovered_os_printers', JSON.stringify(current));
  return current;
};

// Fetch list of printers from the local WebSocket print agent
export const fetchPrintersFromWebSocketAgent = () => {
  return new Promise((resolve, reject) => {
    const wsUrl = localStorage.getItem("printer_agent_url") || "ws://127.0.0.1:8124";
    const ws = new WebSocket(wsUrl);

    const timeout = setTimeout(() => {
      ws.close();
      reject(new Error("Connection timed out"));
    }, 10000);

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'get_printers' }));
    };

    ws.onmessage = (event) => {
      clearTimeout(timeout);
      try {
        const response = JSON.parse(event.data);
        if (response.type === 'get_printers_response') {
          const list = response.printers || [];
          const merged = saveCachedPrinters(list);
          resolve(merged && merged.length > 0 ? merged : list);
        } else {
          reject(new Error("Invalid response type from agent"));
        }
      } catch (err) {
        reject(err);
      } finally {
        ws.close();
      }
    };

    ws.onerror = (err) => {
      clearTimeout(timeout);
      reject(err);
    };
  });
};

// Scan network thermal printers via WebSocket Print Agent
export const scanNetworkPrintersFromWebSocketAgent = (port = 9100) => {
  return new Promise((resolve, reject) => {
    const wsUrl = localStorage.getItem("printer_agent_url") || "ws://127.0.0.1:8124";
    const ws = new WebSocket(wsUrl);

    const timeout = setTimeout(() => {
      ws.close();
      reject(new Error("Subnet scan timed out"));
    }, 12000);

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'scan_network_printers', port }));
    };

    ws.onmessage = (event) => {
      clearTimeout(timeout);
      try {
        const response = JSON.parse(event.data);
        if (response.type === 'scan_network_printers_response') {
          resolve(response.printers || []);
        } else {
          reject(new Error("Invalid response type from agent"));
        }
      } catch (err) {
        reject(err);
      } finally {
        ws.close();
      }
    };

    ws.onerror = (err) => {
      clearTimeout(timeout);
      reject(err);
    };
  });
};

// Helper functions for strict character-width columnar text alignment in thermal printers
const stripPseudoTags = (str) => String(str || "").replace(/<[^>]+>/g, "");

const padEndChar = (str, len) => {
  const s = stripPseudoTags(str);
  return s.length >= len ? s.substring(0, len) : s + " ".repeat(len - s.length);
};

const padStartChar = (str, len) => {
  const s = stripPseudoTags(str);
  return s.length >= len ? s.substring(0, len) : " ".repeat(len - s.length) + s;
};

const formatRow = (col1, col2, width = 32) => {
  const s1 = String(col1 || "");
  const s2 = String(col2 || "");
  const clean1 = stripPseudoTags(s1);
  const clean2 = stripPseudoTags(s2);
  const space = Math.max(1, width - clean1.length - clean2.length);
  return s1 + " ".repeat(space) + s2 + "\n";
};

// Plain text preformatted KOT for raw thermal paper layout
export const generateKOTPlainText = (items, details, pageFormat = '80') => {
  const { table_title, tokenNo, orderId } = details || {};
  const waiterName = details?.waiterName || details?.waiter_name || details?.waiter || details?.created_by || "";
  const headerTitle = details?.printer_type_name || details?.printerTypeName || "KITCHEN ORDER (KOT)";
  const isRoom = details?.deliveryType === 'room' || details?.delivery_type === 'room' || !!details?.room_name || !!details?.source || (table_title && String(table_title).toLowerCase().includes('room'));
  const roomName = details?.room_name || details?.source || (table_title ? String(table_title).replace(/^room\s*/i, '') : '');
  const diningLabel = isRoom
    ? `ROOM: ${roomName ? (String(roomName).toLowerCase().startsWith('room') ? String(roomName).toUpperCase() : `ROOM ${String(roomName).toUpperCase()}`) : 'ROOM ORDER'}`
    : (table_title ? `TABLE: ${String(table_title).toUpperCase()}` : (details?.deliveryType ? String(details.deliveryType).toUpperCase() : "TAKEAWAY / DELIVERY"));

  const is58mm = String(pageFormat).includes('58') || pageFormat === '58' || Number(pageFormat) <= 60;
  const colWidth = is58mm ? 32 : 42;

  const separator = "-".repeat(colWidth) + "\n";
  const doubleSeparator = "=".repeat(colWidth) + "\n";
  
  let txt = "";
  txt += doubleSeparator;
  txt += `<center><b><big>${headerTitle.toUpperCase()}</big></b></center>\n`;
  if (details?.copyLabel) {
    txt += `<center><b>[ ${details.copyLabel} ]</b></center>\n`;
  }
  txt += doubleSeparator;
  txt += `<center><b><big>${diningLabel}</big></b></center>\n`;
  txt += `<center><b>TOKEN: ${tokenNo || "-"} | ORDER #${orderId || "-"}</b></center>\n`;
  if (waiterName) {
    txt += `<b>WAITER: ${waiterName.toUpperCase()}</b>\n`;
  }
  txt += `DATE: ${new Date().toLocaleString()}\n`;
  txt += separator;
  
  // Column Headers: QTY  ITEM
  if (is58mm) {
    txt += "QTY  ITEM\n";
  } else {
    txt += "QTY   ITEM / PARTICULARS\n";
  }
  txt += separator;

  (items || []).forEach((item) => {
    const vTitle = item.variant_title || item.variant?.title || (typeof item.variant === 'string' ? item.variant : '') || '';
    const itemTitle = item.title || item.item_title || '';
    const qty = item.quantity ?? item.qty ?? 1;

    txt += `<b><big>[ ${qty}x ] ${itemTitle}</big></b>\n`;
    if (vTitle) {
      txt += `<b>   >> VARIANT: ${vTitle}</b>\n`;
    }
    if (item.addons && item.addons.length > 0) {
      const addonList = Array.isArray(item.addons)
        ? item.addons.map(a => typeof a === 'object' ? (a.title || a.name || '') : String(a)).filter(Boolean).join(", ")
        : String(item.addons);
      if (addonList) {
        txt += `<b>   + ADDONS: ${addonList}</b>\n`;
      }
    }
    if (item.notes) {
      txt += `<b>   *** NOTE: ${item.notes} ***</b>\n`;
    }
    txt += separator;
  });
  
  txt += `<center>--- END OF ${headerTitle.toUpperCase()} ---</center>\n`;
  txt += doubleSeparator;
  return txt;
};

function __groupReceiptItems(items) {
  if (!items || !Array.isArray(items)) return items;
  const grouped = [];
  items.forEach(item => {
    const addonsKey = Array.isArray(item.addons_ids) 
      ? item.addons_ids.slice().sort().join(',') 
      : (Array.isArray(item.addons) ? item.addons.map(a=>a.id || a).sort().join(',') : '');
    const priceKey = item.price || 0;
    const variantKey = item.variant?.title || item.variant_title || '';
    const key = (item.title || item.item_title || '') + '_' + variantKey + '_' + addonsKey + '_' + priceKey;
    
    const existing = grouped.find(g => g._groupKey === key);
    if (existing) {
      existing.quantity = Number(existing.quantity) + Number(item.quantity || 1);
    } else {
      grouped.push({ ...item, _groupKey: key, quantity: Number(item.quantity || 1) });
    }
  });
  return grouped;
}

// -------------------------------------------------------------
// Format 0: NEPAL RETAIL TAX INVOICE (SMTECH / POS 80MM EXACT REPLICA)
// -------------------------------------------------------------
export const generateRetailTaxInvoicePlainText = (details, pageFormat = '80') => {
  const is58mm = String(pageFormat).includes('58') || pageFormat === '58' || Number(pageFormat) <= 60;
  const colWidth = is58mm ? 32 : 42;
  const sep = "-".repeat(colWidth) + "\n";
  const doubleSep = "=".repeat(colWidth) + "\n";

  const {
    cartItems = [], deliveryType = '', customerType = '', customer = null, currency = 'Rs.',
    storeSettings = {}, printSettings = {}, store_name, store_address, store_phone,
    itemsTotal = 0, taxTotal = 0, serviceChargeTotal = 0, payableTotal = 0,
    storedDiscount = 0, roundOff = 0, paidAmount, dueAmount,
    tokenNo = '', orderId = '', paymentMethod = '', multiPayments = [],
    isEstimate = false
  } = details || {};

  const storeName = store_name || storeSettings?.store_name || "RESTAURANT";
  const storeAddress = store_address || storeSettings?.address || "";
  const storePhone = store_phone || storeSettings?.phone || "";
  const sellerPan = storeSettings?.pan_no || storeSettings?.panNo || details?.pan_no || "";
  const isVat = Boolean(storeSettings?.is_vat_registered || storeSettings?.isVatRegistered);
  const curSymbol = typeof currency === 'object' && currency?.symbol ? currency.symbol : (currency || 'Rs.');

  const rawCustomerName = customer?.name || details?.customerName || details?.customer_name || details?.guest_name || '';
  const rawCustomerId = customer?.id || details?.customerId || details?.customer_id || null;
  const hasCustomer = Boolean(rawCustomerId && String(rawCustomerId).trim().toUpperCase() !== 'WALK-IN' && String(rawCustomerId).trim().toUpperCase() !== 'WALKIN');
  const isWalkinName = !rawCustomerName || ['walkin', 'walk-in', 'walk-in guest', 'walk-in customer', 'walk in', 'cash customer', 'guest'].includes(rawCustomerName.trim().toLowerCase());
  const isWalkin = !hasCustomer && isWalkinName;
  const displayCustomerName = isWalkin ? 'Walk-in' : (rawCustomerName || 'Walk-in');
  const displayCustomerType = isWalkin ? 'Walk-in' : (customerType || details?.customer_type || 'Customer');
  const buyerPan = customer?.pan_no || details?.buyerPan || details?.buyer_pan || '';

  const cleanTitle = (details.table_title || details.tableName || details.room_name || details.source || '').trim();
  const areaName = details.area || (deliveryType === 'takeaway' ? 'TAKEAWAY' : (deliveryType === 'delivery' ? 'DELIVERY' : 'HALL'));
  const now = new Date();
  const bsDate = getNepaliDateBS(details.billedTime || details.date || now);
  const fiscalYear = getNepaliFiscalYear(details.billedTime || details.date || now);

  const subTotalNum = Number(String(itemsTotal || 0).replace(/,/g, ''));
  const discountNum = Number(String(storedDiscount || 0).replace(/,/g, ''));
  const scNum = Number(String(serviceChargeTotal || 0).replace(/,/g, ''));
  const taxableAmt = details.taxableAmount !== undefined && details.taxableAmount !== null
    ? Number(String(details.taxableAmount).replace(/,/g, ''))
    : Math.max(0, subTotalNum - discountNum + scNum);
  const taxNum = Number(String(taxTotal || 0).replace(/,/g, ''));
  const roundOffNum = Number(String(roundOff || 0).replace(/,/g, ''));
  const grandTotalNum = Number(String(payableTotal || 0).replace(/,/g, ''));
  const discountPercent = subTotalNum > 0 && discountNum > 0 ? Math.round((discountNum / subTotalNum) * 100) : 0;
  const wordsOnly = numberToWords(grandTotalNum, "").replace(/Rupees/gi, "").trim();

  let txt = "";
  txt += `<center><b><big>${storeName}</big></b></center>\n`;
  if (storeAddress) txt += `<center>${storeAddress}</center>\n`;
  if (storePhone) txt += `<center>Tel: ${storePhone}</center>\n`;
  txt += `<center><b>${isEstimate ? 'ESTIMATE BILL' : 'TAX INVOICE'}</b></center>\n`;
  txt += sep;

  if (sellerPan) txt += formatRow(`PAN: ${sellerPan}`, buyerPan ? `Buyer PAN: ${buyerPan}` : "", colWidth);
  else if (buyerPan) txt += `Buyer PAN: ${buyerPan}\n`;
  txt += formatRow("Bill No:", details.billNo || orderId || "-", colWidth);
  txt += formatRow("Area:", areaName, colWidth);
  if (cleanTitle) txt += formatRow("Room/Table:", cleanTitle, colWidth);
  txt += formatRow("Customer Type:", displayCustomerType, colWidth);
  txt += formatRow("Customer:", displayCustomerName, colWidth);
  if (customer?.phone) txt += formatRow("Contact:", customer.phone, colWidth);
  txt += formatRow("Date:", formatReceiptDateTime(details.billedTime || details.date || now), colWidth);
  txt += formatRow("Miti:", bsDate, colWidth);
  txt += formatRow("Fiscal Year:", fiscalYear, colWidth);
  txt += sep;

  // Item Table Header
  if (is58mm) {
    txt += "Item           QTY  Rate   Total\n";
  } else {
    txt += "Item                 QTY   Rate    Total\n";
  }
  txt += sep;

  cartItems.forEach(item => {
    const qty = Number(item.quantity || item.qty || 1);
    const safeNum = (val) => Number(String(val || 0).replace(/,/g, '')) || 0;
    const rate = safeNum(item.price || item.rate);
    const total = qty * rate;

    const itemTitle = item.title || item.item_title || '';
    const vTitle = item.variant_title || item.variant?.title || '';
    const fullTitle = itemTitle + (vTitle ? ` (${vTitle})` : '');

    if (is58mm) {
      // 32 chars: Item (13) QTY (4) Rate (7) Total (8)
      const cQty = padStartChar(String(qty), 4);
      const cRate = padStartChar(rate.toFixed(2), 7);
      const cTot = padStartChar(total.toFixed(2), 8);
      if (fullTitle.length <= 13) {
        const cItem = padEndChar(fullTitle, 13);
        txt += `${cItem}${cQty}${cRate}${cTot}\n`;
      } else {
        txt += `${fullTitle}\n`;
        txt += `${" ".repeat(13)}${cQty}${cRate}${cTot}\n`;
      }
    } else {
      // 42 chars: Item (19) QTY (5) Rate (8) Total (10)
      const cQty = padStartChar(String(qty), 5);
      const cRate = padStartChar(rate.toFixed(2), 8);
      const cTot = padStartChar(total.toFixed(2), 10);
      if (fullTitle.length <= 19) {
        const cItem = padEndChar(fullTitle, 19);
        txt += `${cItem}${cQty}${cRate}${cTot}\n`;
      } else {
        txt += `${fullTitle}\n`;
        txt += `${" ".repeat(19)}${cQty}${cRate}${cTot}\n`;
      }
    }

    if (item.addons && item.addons.length > 0) {
      const addonList = Array.isArray(item.addons)
        ? item.addons.map(a => typeof a === 'object' ? (a.title || a.name || '') : String(a)).filter(Boolean).join(", ")
        : String(item.addons);
      if (addonList) txt += `  + ${addonList}\n`;
    }
    if (item.notes) {
      txt += `  * ${item.notes}\n`;
    }
  });

  txt += sep;

  // Totals breakdown
  txt += formatRow("Sub-Total :", subTotalNum.toFixed(2), colWidth);
  if (discountNum > 0) {
    const discLabel = `Discount ${discountPercent > 0 ? `(${discountPercent}%)` : ''} :`;
    txt += formatRow(discLabel, `-${discountNum.toFixed(2)}`, colWidth);
  }
  if (scNum > 0) {
    txt += formatRow("Service Charge :", `+${scNum.toFixed(2)}`, colWidth);
  }
  txt += formatRow((isVat || taxNum > 0) ? "Taxable AMT :" : "NonTaxable AMT :", taxableAmt.toFixed(2), colWidth);
  if (taxNum > 0) {
    txt += formatRow(isVat ? "13% VAT :" : "Tax :", `+${taxNum.toFixed(2)}`, colWidth);
  }
  if (roundOffNum !== 0) {
    txt += formatRow("Round Off :", (roundOffNum > 0 ? `+` : ``) + roundOffNum.toFixed(2), colWidth);
  }

  txt += doubleSep;
  txt += formatRow("<b><big>Grand Total:</big></b>", `<b><big>${grandTotalNum.toFixed(2)}</big></b>`, colWidth);
  txt += doubleSep;

  // Mode of payment
  let modeStr = paymentMethod || 'Cash';
  if (multiPayments && multiPayments.length > 0) {
    modeStr = multiPayments.map(p => `${p.title || 'Other'}(${Number(p.amount).toFixed(2)})`).join(', ');
  }
  txt += formatRow("Mode:", modeStr, colWidth);
  txt += formatRow("Received AMT:", Number(paidAmount !== undefined ? paidAmount : grandTotalNum).toFixed(2), colWidth);
  if (Number(dueAmount || 0) > 0) {
    txt += formatRow("<b>Balance Due:</b>", `<b>${Number(dueAmount).toFixed(2)}</b>`, colWidth);
  }

  if (wordsOnly) {
    txt += `In Words: ${wordsOnly} Only\n`;
  }
  const note = details.notes || details.note || "";
  if (note) {
    txt += `Note: ${note}\n`;
  }

  txt += sep;
  txt += "<center>Thank You.</center>\n\n";

  // Dual Signatures
  const half = Math.floor(colWidth / 2);
  const cashierLine = padEndChar("----------", half);
  const custLine = padStartChar("----------", colWidth - half);
  txt += `${cashierLine}${custLine}\n`;
  const cashierLabel = padEndChar("Cashier", half);
  const custLabel = padStartChar("Customer", colWidth - half);
  txt += `${cashierLabel}${custLabel}\n\n`;

  // STJ Technologies branding
  txt += "<center>Powered By : STJ Technologies</center>\n";
  txt += "<center>www.ehospitalitynepal.com</center>\n";

  return txt;
};

// -------------------------------------------------------------
// Format 1: IRD STANDARD THERMAL (80mm / 58mm)
// -------------------------------------------------------------
export const generateThermalIRDPlainText = (details, pageFormat = '80') => {
  const is58mm = String(pageFormat).includes('58') || pageFormat === '58' || Number(pageFormat) <= 60;
  const colWidth = is58mm ? 32 : 42;
  const sep = "-".repeat(colWidth) + "\n";
  const doubleSep = "=".repeat(colWidth) + "\n";

  const {
    cartItems = [], deliveryType = '', customer = null, currency = 'Rs.',
    storeSettings = {}, printSettings = {}, store_name, store_address, store_phone,
    itemsTotal = 0, taxTotal = 0, serviceChargeTotal = 0, payableTotal = 0,
    storedDiscount = 0, roundOff = 0, paidAmount, dueAmount,
    tokenNo = '', orderId = '', paymentMethod = '', isEstimate = false
  } = details || {};

  const storeName = store_name || storeSettings?.store_name || "RESTAURANT";
  const storeAddress = store_address || storeSettings?.address || "";
  const storePhone = store_phone || storeSettings?.phone || "";
  const sellerPan = storeSettings?.pan_no || storeSettings?.panNo || details?.pan_no || "";
  const isVat = Boolean(storeSettings?.is_vat_registered || storeSettings?.isVatRegistered);

  const rawCustomerName = customer?.name || details?.customerName || details?.customer_name || 'Walk-in';
  const buyerPan = customer?.pan_no || details?.buyerPan || details?.buyer_pan || '';
  const now = new Date();
  const bsDate = getNepaliDateBS(details.billedTime || details.date || now);
  const fiscalYear = getNepaliFiscalYear(details.billedTime || details.date || now);

  const subTotalNum = Number(String(itemsTotal || 0).replace(/,/g, ''));
  const discountNum = Number(String(storedDiscount || 0).replace(/,/g, ''));
  const scNum = Number(String(serviceChargeTotal || 0).replace(/,/g, ''));
  const taxableAmt = details.taxableAmount !== undefined && details.taxableAmount !== null
    ? Number(String(details.taxableAmount).replace(/,/g, ''))
    : Math.max(0, subTotalNum - discountNum + scNum);
  const taxNum = Number(String(taxTotal || 0).replace(/,/g, ''));
  const roundOffNum = Number(String(roundOff || 0).replace(/,/g, ''));
  const grandTotalNum = Number(String(payableTotal || 0).replace(/,/g, ''));
  const amountInWords = numberToWords(grandTotalNum, "Rupees");

  let txt = "";
  txt += `<center><b><big>${storeName}</big></b></center>\n`;
  if (storeAddress) txt += `<center>${storeAddress}</center>\n`;
  if (storePhone) txt += `<center>Tel: ${storePhone}</center>\n`;
  if (sellerPan) txt += `<center><b>${isVat ? "VAT" : "PAN"} No: ${sellerPan}</b></center>\n`;
  txt += sep;
  txt += `<center><b>${isEstimate ? "ESTIMATE BILL / अनुमानित बिल" : (isVat ? "TAX INVOICE / कर बिजक" : "ABBREVIATED TAX INVOICE / कर बिजक")}</b></center>\n`;
  if (isEstimate) {
    txt += "<center>*** NOT A TAX INVOICE (यो कर बिजक होइन) ***</center>\n";
  }
  txt += sep;

  txt += formatRow(isEstimate ? "Estimate Ref:" : "Invoice No:", details.billNo || orderId || "-", colWidth);
  txt += formatRow("Fiscal Year (आ.व.):", fiscalYear, colWidth);
  txt += formatRow("Date/Time:", formatReceiptDateTime(details.billedTime || details.date || now), colWidth);
  txt += formatRow("Miti:", bsDate, colWidth);
  const cleanTitle = (details.table_title || details.tableName || details.room_name || details.source || '').trim();
  if (cleanTitle) txt += formatRow("Room/Table:", cleanTitle, colWidth);
  txt += formatRow("Buyer's Name:", rawCustomerName, colWidth);
  if (buyerPan) txt += formatRow("Buyer's PAN:", buyerPan, colWidth);
  txt += sep;

  // Items table
  txt += is58mm ? "SN Particulars      Qty   Amount\n" : "SN Particulars           Qty    Amount\n";
  txt += sep;

  cartItems.forEach((it, idx) => {
    const qty = Number(it.quantity || it.qty || 1);
    const safeNum = (val) => Number(String(val || 0).replace(/,/g, '')) || 0;
    const rate = safeNum(it.price || it.rate);
    const tot = (qty * rate).toFixed(2);
    const fullTitle = (it.title || it.item_title || '') + (it.variant_title ? ` (${it.variant_title})` : '');

    const sn = String(idx + 1).padEnd(2);
    if (is58mm) {
      const q = padStartChar(String(qty), 4);
      const a = padStartChar(tot, 10);
      if (fullTitle.length <= 14) {
        const part = padEndChar(fullTitle, 14);
        txt += `${sn} ${part}${q}${a}\n`;
      } else {
        txt += `${sn} ${fullTitle}\n`;
        txt += `   ${" ".repeat(14)}${q}${a}\n`;
      }
    } else {
      const q = padStartChar(String(qty), 5);
      const a = padStartChar(tot, 12);
      if (fullTitle.length <= 20) {
        const part = padEndChar(fullTitle, 20);
        txt += `${sn} ${part}${q}${a}\n`;
      } else {
        txt += `${sn} ${fullTitle}\n`;
        txt += `   ${" ".repeat(20)}${q}${a}\n`;
      }
    }
  });

  txt += sep;
  txt += formatRow("Gross Total:", subTotalNum.toFixed(2), colWidth);
  if (discountNum > 0) txt += formatRow("Discount:", `-${discountNum.toFixed(2)}`, colWidth);
  if (scNum > 0) txt += formatRow("Service Charge:", `+${scNum.toFixed(2)}`, colWidth);
  if (roundOffNum !== 0) txt += formatRow("Round Off:", (roundOffNum > 0 ? `+` : ``) + roundOffNum.toFixed(2), colWidth);
  txt += formatRow("Taxable Amount:", taxableAmt.toFixed(2), colWidth);
  if (taxNum > 0) txt += formatRow(isVat ? "13% VAT:" : "Tax:", `+${taxNum.toFixed(2)}`, colWidth);

  txt += doubleSep;
  txt += formatRow("<b>Net Payable:</b>", `<b>${grandTotalNum.toFixed(2)}</b>`, colWidth);
  txt += doubleSep;

  if (paymentMethod) txt += formatRow("Payment Mode:", paymentMethod, colWidth);
  txt += formatRow("Total Paid:", Number(paidAmount !== undefined ? paidAmount : grandTotalNum).toFixed(2), colWidth);
  if (Number(dueAmount || 0) > 0) {
    txt += formatRow("<b>Balance Due:</b>", `<b>${Number(dueAmount).toFixed(2)}</b>`, colWidth);
  }

  txt += `In Words:\n${amountInWords}\n`;
  txt += sep;
  txt += `<center>${isEstimate ? '*** ESTIMATE BILL ONLY — NOT A TAX INVOICE ***' : 'THIS IS A COMPUTER GENERATED TAX INVOICE'}</center>\n`;
  txt += "<center>Thank you for your visit!</center>\n";
  return txt;
};

// -------------------------------------------------------------
// Format 2: MODERN COMPACT POS (80mm / 58mm)
// -------------------------------------------------------------
export const generateModernPOSPlainText = (details, pageFormat = '80') => {
  const is58mm = String(pageFormat).includes('58') || pageFormat === '58' || Number(pageFormat) <= 60;
  const colWidth = is58mm ? 32 : 42;
  const sep = "-".repeat(colWidth) + "\n";
  const doubleSep = "=".repeat(colWidth) + "\n";

  const {
    cartItems = [], customer = null, storeSettings = {},
    store_name, store_address, store_phone, itemsTotal = 0, taxTotal = 0,
    serviceChargeTotal = 0, payableTotal = 0, storedDiscount = 0, roundOff = 0,
    paidAmount, dueAmount, tokenNo = '', orderId = '', paymentMethod = '', isEstimate = false
  } = details || {};

  const storeName = store_name || storeSettings?.store_name || "RESTAURANT";
  const storeAddress = store_address || storeSettings?.address || "";
  const storePhone = store_phone || storeSettings?.phone || "";
  const sellerPan = storeSettings?.pan_no || storeSettings?.panNo || details?.pan_no || "";
  const isVat = Boolean(storeSettings?.is_vat_registered || storeSettings?.isVatRegistered);
  const now = new Date();

  const subTotalNum = Number(String(itemsTotal || 0).replace(/,/g, ''));
  const discountNum = Number(String(storedDiscount || 0).replace(/,/g, ''));
  const scNum = Number(String(serviceChargeTotal || 0).replace(/,/g, ''));
  const taxableAmt = details.taxableAmount !== undefined && details.taxableAmount !== null
    ? Number(String(details.taxableAmount).replace(/,/g, ''))
    : Math.max(0, subTotalNum - discountNum + scNum);
  const taxNum = Number(String(taxTotal || 0).replace(/,/g, ''));
  const grandTotalNum = Number(String(payableTotal || 0).replace(/,/g, ''));

  let txt = "";
  txt += `<center><b>[ ${isEstimate ? "ESTIMATE BILL" : (isVat ? "TAX INVOICE" : "RECEIPT")} ]</b></center>\n`;
  txt += `<center><b><big>${storeName}</big></b></center>\n`;
  if (storeAddress) txt += `<center>${storeAddress}</center>\n`;
  if (storePhone) txt += `<center>Phone: ${storePhone}</center>\n`;
  if (sellerPan) txt += `<center>${isVat ? "VAT" : "PAN"}: ${sellerPan}</center>\n`;
  txt += sep;

  txt += formatRow("Ref No:", details.billNo || orderId || "-", colWidth);
  txt += formatRow("Date:", formatReceiptDateTime(details.billedTime || details.date || now), colWidth);
  const cleanTitle = (details.table_title || details.tableName || details.room_name || details.source || '').trim();
  if (cleanTitle) txt += formatRow("Dining:", cleanTitle, colWidth);
  if (customer?.name) txt += formatRow("Guest:", customer.name, colWidth);
  if (tokenNo) txt += formatRow("Token:", tokenNo, colWidth);
  txt += sep;

  // Items
  cartItems.forEach(item => {
    const qty = Number(item.quantity || item.qty || 1);
    const safeNum = (val) => Number(String(val || 0).replace(/,/g, '')) || 0;
    const rate = safeNum(item.price || item.rate);
    const lineTot = (qty * rate).toFixed(2);
    const title = item.title || item.item_title || '';
    const leftSide = `${qty}x ${title}`;
    txt += formatRow(leftSide.substring(0, colWidth - 10), lineTot, colWidth);
  });

  txt += sep;
  txt += formatRow("Subtotal:", subTotalNum.toFixed(2), colWidth);
  if (discountNum > 0) txt += formatRow("Discount:", `-${discountNum.toFixed(2)}`, colWidth);
  if (scNum > 0) txt += formatRow("Service Charge:", `+${scNum.toFixed(2)}`, colWidth);
  txt += formatRow("Taxable Amount:", taxableAmt.toFixed(2), colWidth);
  if (taxNum > 0) txt += formatRow(isVat ? "VAT (13%):" : "Tax:", `+${taxNum.toFixed(2)}`, colWidth);

  txt += doubleSep;
  txt += formatRow("<b><big>Total Payable:</big></b>", `<b><big>${grandTotalNum.toFixed(2)}</big></b>`, colWidth);
  txt += doubleSep;

  if (paymentMethod) txt += formatRow("Payment Mode:", paymentMethod, colWidth);
  txt += formatRow("Paid:", Number(paidAmount !== undefined ? paidAmount : grandTotalNum).toFixed(2), colWidth);
  if (Number(dueAmount || 0) > 0) {
    txt += formatRow("<b>Balance Due:</b>", `<b>${Number(dueAmount).toFixed(2)}</b>`, colWidth);
  }

  txt += sep;
  txt += `<center>${isEstimate ? "PROFORMA FOLIO • NOT A TAX INVOICE" : "Computer Generated • IRD Compliant"}</center>\n`;
  txt += "<center>Thank you for visiting!</center>\n";
  return txt;
};

// -------------------------------------------------------------
// Format 3: STANDARD POS THERMAL RECEIPT (ORDER PAGE FORMAT)
// -------------------------------------------------------------
export const generatePOSReceiptPlainText = (details, pageFormat = '80') => {
  const is58mm = String(pageFormat).includes('58') || pageFormat === '58' || Number(pageFormat) <= 60;
  const colWidth = is58mm ? 32 : 42;
  const sep = "-".repeat(colWidth) + "\n";
  const doubleSep = "=".repeat(colWidth) + "\n";

  const { 
    cartItems = [], deliveryType = '', customerType = '', customer = null, currency = 'Rs.',
    storeSettings = {}, printSettings = {}, store_name, store_address, store_phone,
    orderId = '', tokenNo = '', paymentMethod = '', itemsTotal = 0, taxTotal = 0,
    serviceChargeTotal = 0, payableTotal = 0, storedDiscount = 0, roundOff = 0,
    paidAmount = undefined, dueAmount = 0, isEstimate = false
  } = details || {};

  const storeName = store_name || storeSettings?.store_name || "RESTAURANT";
  const storeAddress = store_address || storeSettings?.address || "";
  const storePhone = store_phone || storeSettings?.phone || "";
  const panNo = storeSettings?.pan_no || storeSettings?.panNo || details?.pan_no || "";
  const isVat = Boolean(storeSettings?.is_vat_registered || storeSettings?.isVatRegistered);
  const curSymbol = typeof currency === 'object' && currency?.symbol ? currency.symbol : (currency || 'Rs.');

  const header = printSettings?.header || "";
  const footer = printSettings?.footer || "Thank You! Visit Again";
  
  const rawCustomerName = customer?.name || details?.customerName || details?.customer_name || details?.guest_name || '';
  const rawCustomerId = customer?.id || details?.customerId || details?.customer_id || null;
  const hasCustomer = Boolean(rawCustomerId && String(rawCustomerId).trim().toUpperCase() !== 'WALK-IN' && String(rawCustomerId).trim().toUpperCase() !== 'WALKIN');
  const isWalkinName = !rawCustomerName || ['walkin', 'walk-in', 'walk-in guest', 'walk-in customer', 'walk in', 'cash customer', 'guest'].includes(rawCustomerName.trim().toLowerCase());
  const isWalkin = !hasCustomer && isWalkinName;
  const displayCustomerType = isWalkin ? 'Walk-in' : (customerType || details?.customer_type || 'Customer');
  const displayCustomerName = isWalkin ? 'Walk-in' : (rawCustomerName || 'Walk-in');

  let txt = "";
  txt += `<center><b><big>${storeName}</big></b></center>\n`;
  if (storeAddress) txt += `<center>${storeAddress}</center>\n`;
  if (storePhone) txt += `<center>Tel: ${storePhone}</center>\n`;
  if (panNo) txt += `<center><b>${isVat ? 'VAT' : 'PAN'}: ${panNo}</b></center>\n`;
  if (header) txt += `\n${header}\n`;
  txt += sep;

  txt += `<center><b>${isEstimate ? 'PROFORMA ESTIMATE (अनुमानित बिल)' : (isVat ? 'TAX INVOICE (कर बिजक)' : 'INVOICE')}</b></center>\n`;
  txt += sep;

  if (orderId) txt += formatRow("Order #:", String(orderId), colWidth);
  if (details.billNo) txt += formatRow("Bill No:", String(details.billNo), colWidth);
  if (tokenNo) txt += formatRow("Token:", String(tokenNo), colWidth);
  
  const cleanTitle = (details.table_title || details.tableName || details.room_name || details.source || '').trim();
  if (cleanTitle) txt += formatRow("Room/Table:", cleanTitle, colWidth);
  
  txt += formatRow("Type:", displayCustomerType, colWidth);
  txt += formatRow("Customer:", displayCustomerName, colWidth);
  if (customer?.pan_no) txt += formatRow("Buyer PAN:", customer.pan_no, colWidth);
  if (customer?.phone) txt += formatRow("Phone:", customer.phone, colWidth);

  const printDateStr = formatReceiptDateTime(details.billedTime || details.date || details.created_at || new Date());
  txt += formatRow("Billed:", printDateStr, colWidth);
  txt += sep;

  // Items table
  if (is58mm) {
    txt += "QTY ITEM               PRICE\n";
  } else {
    txt += "QTY  ITEM                     PRICE\n";
  }
  txt += sep;

  cartItems?.forEach(item => {
    const qty = String(item.quantity || item.qty || 1);
    const itemTitle = item.title || item.item_title || '';
    const variantTitle = item.variant_title || item.variant?.title || '';
    const safeNum = (val) => Number(String(val || 0).replace(/,/g, '')) || 0;
    const pBase = safeNum(item.price || item.rate);
    const priceVal = pBase * safeNum(item.quantity || item.qty || 1);
    const priceStr = priceVal.toFixed(2);

    const fullTitle = itemTitle + (variantTitle ? ` - ${variantTitle}` : '');
    const maxTitleLen = Math.max(10, colWidth - 5 - priceStr.length - 1);
    const titlePadded = padEndChar(fullTitle, maxTitleLen);

    txt += `${padEndChar(qty, 4)}${titlePadded} ${priceStr}\n`;
    
    if (item.addons && item.addons.length > 0) {
      txt += `   + Addons\n`;
    }
  });
  
  txt += sep;
  txt += formatRow("Subtotal:", Number(itemsTotal || 0).toFixed(2), colWidth);
  if (Number(storedDiscount || 0) > 0) txt += formatRow("Discount:", `-${Number(storedDiscount).toFixed(2)}`, colWidth);
  if (serviceChargeTotal > 0) txt += formatRow("Service Charge:", `+${Number(serviceChargeTotal).toFixed(2)}`, colWidth);
  
  if (isVat || taxTotal > 0) {
    const taxable = details.taxableAmount !== undefined ? details.taxableAmount : Math.max(0, Number(itemsTotal || 0) - Number(storedDiscount || 0) + Number(serviceChargeTotal || 0));
    txt += formatRow("Taxable Amount:", Number(taxable).toFixed(2), colWidth);
  }

  if (taxTotal > 0) txt += formatRow(isVat ? "13% VAT:" : "Tax:", `+${Number(taxTotal).toFixed(2)}`, colWidth);
  if (Number(roundOff || 0) !== 0) txt += formatRow("Round Off:", (Number(roundOff) > 0 ? '+' : '') + Number(roundOff).toFixed(2), colWidth);
  
  txt += doubleSep;
  txt += formatRow("<b><big>TOTAL:</big></b>", `<b><big>${Number(payableTotal || 0).toFixed(2)}</big></b>`, colWidth);
  txt += doubleSep;
  
  if (paymentMethod) txt += formatRow("Payment Mode:", paymentMethod, colWidth);
  if (Number(paidAmount || 0) > 0 && Number(paidAmount || 0) !== Number(payableTotal || 0)) {
    txt += formatRow("Received:", Number(paidAmount).toFixed(2), colWidth);
  }
  if (Number(dueAmount || 0) > 0) {
    txt += formatRow("<b>Due Balance:</b>", `<b>${Number(dueAmount).toFixed(2)}</b>`, colWidth);
  }
  const receiptNote = details.notes || details.note || "";
  if (receiptNote) {
    txt += `Note: ${receiptNote}\n`;
  }
  txt += sep;
  
  if (footer) txt += `<center>${footer}</center>\n`;
  txt += `<center>Thank You!</center>\n`;
  return txt;
};

// Main generator routing to the user-selected Bill Format from Web Settings
export const generateReceiptPlainText = (details, pageFormatOverride = null) => {
  details.cartItems = __groupReceiptItems(details.cartItems);
  const pageFormat = pageFormatOverride || details.pageFormat || details.page_format || details.printSettings?.page_format || localStorage.getItem('page_format') || '80';
  const selectedFormat = localStorage.getItem('bill_format') || details.bill_format || details.billFormat || details.printSettings?.bill_format || details.printSettings?.billFormat || 'retail_tax_invoice';

  if (selectedFormat === 'retail_tax_invoice') {
    return generateRetailTaxInvoicePlainText(details, pageFormat);
  }
  if (selectedFormat === 'thermal_ird') {
    return generateThermalIRDPlainText(details, pageFormat);
  }
  if (selectedFormat === 'modern_pos') {
    return generateModernPOSPlainText(details, pageFormat);
  }
  // Default to pos_receipt
  return generatePOSReceiptPlainText(details, pageFormat);
};

export const printReceiptViaWebBluetooth = async (details) => {
  const plainText = generateReceiptPlainText(details);
  await printViaWebBluetooth(null, plainText);
};

export const getElectron = () => {
  if (typeof window === 'undefined') return null;
  if (window.electron) return window.electron;
  if (typeof window.require === 'function') {
    try {
      const el = window.require('electron');
      if (el) return el;
    } catch (e) {}
  }
  return null;
};

export const isElectronEnv = () => {
  return typeof window !== 'undefined' && Boolean(
    window.isElectron ||
    window.electron ||
    (window.require && typeof window.require === 'function') ||
    (window.process && window.process.type) ||
    (navigator.userAgent && navigator.userAgent.toLowerCase().includes('electron'))
  );
};

export const DEFAULT_PRINTER_TYPE_ID = "default";

/**
 * Returns all configured Printer Types from localStorage.
 * Ensures the 'default' printer type always exists (matching Flutter KDS).
 */
export const getPrinterTypes = () => {
  let types = [];
  try {
    const raw = localStorage.getItem("kds_printer_types") || localStorage.getItem("restro_printer_types");
    if (raw) {
      types = JSON.parse(raw);
    }
  } catch (e) {
    console.error("Error parsing printer types:", e);
  }

  if (!Array.isArray(types)) types = [];

  const defaultBillPrinter = localStorage.getItem("billPrinter") || localStorage.getItem("kds_os_printer_name") || "";
  const defaultCopies = parseInt(localStorage.getItem("kds_default_copies") || localStorage.getItem("printCount") || "1") || 1;
  const defaultFormat = localStorage.getItem("page_format") || "80";
  const isOs = localStorage.getItem("kds_is_os_printer") !== "false";
  const ipAddress = localStorage.getItem("kds_ip_printer_address") || "";

  const defaultIndex = types.findIndex(p => p.id === DEFAULT_PRINTER_TYPE_ID);
  if (defaultIndex === -1) {
    types.unshift({
      id: DEFAULT_PRINTER_TYPE_ID,
      name: "Default Printer",
      isOsPrinter: isOs,
      osPrinterName: defaultBillPrinter,
      ipPrinterAddress: ipAddress,
      copies: defaultCopies,
      paperFormat: defaultFormat,
    });
  } else {
    if (!types[defaultIndex].osPrinterName && defaultBillPrinter) {
      types[defaultIndex].osPrinterName = defaultBillPrinter;
    }
  }

  return types;
};

/**
 * Saves or updates a Printer Type in localStorage.
 */
export const savePrinterType = (type) => {
  if (!type || !type.id) return;
  const types = getPrinterTypes();
  const index = types.findIndex(p => p.id === type.id);

  const cleanType = {
    id: type.id,
    name: type.id === DEFAULT_PRINTER_TYPE_ID ? "Default Printer" : (type.name || "").trim(),
    isOsPrinter: Boolean(type.isOsPrinter ?? true),
    osPrinterName: (type.osPrinterName || "").trim(),
    ipPrinterAddress: (type.ipPrinterAddress || "").trim(),
    copies: Math.max(1, parseInt(type.copies) || 1),
    paperFormat: String(type.paperFormat || "80"),
  };

  if (index >= 0) {
    types[index] = cleanType;
  } else {
    types.push(cleanType);
  }

  localStorage.setItem("kds_printer_types", JSON.stringify(types));
  localStorage.setItem("restro_printer_types", JSON.stringify(types));

  if (type.id === DEFAULT_PRINTER_TYPE_ID) {
    localStorage.setItem("billPrinter", cleanType.osPrinterName);
    localStorage.setItem("kds_os_printer_name", cleanType.osPrinterName);
    localStorage.setItem("kds_ip_printer_address", cleanType.ipPrinterAddress);
    localStorage.setItem("kds_is_os_printer", cleanType.isOsPrinter.toString());
    localStorage.setItem("kds_default_copies", cleanType.copies.toString());
    localStorage.setItem("printCount", cleanType.copies.toString());
    localStorage.setItem("page_format", cleanType.paperFormat);
  }

  return types;
};

/**
 * Deletes a Printer Type and unassigns any categories mapped to it.
 */
export const deletePrinterType = (id) => {
  if (!id || id === DEFAULT_PRINTER_TYPE_ID) return;
  let types = getPrinterTypes().filter(p => p.id !== id);
  localStorage.setItem("kds_printer_types", JSON.stringify(types));
  localStorage.setItem("restro_printer_types", JSON.stringify(types));

  const assignments = getCategoryAssignments();
  let changed = false;
  Object.keys(assignments).forEach(catId => {
    if (assignments[catId] === id) {
      delete assignments[catId];
      changed = true;
    }
  });
  if (changed) {
    localStorage.setItem("kds_category_assignments", JSON.stringify(assignments));
    localStorage.setItem("restro_category_assignments", JSON.stringify(assignments));
  }
  return types;
};

/**
 * Gets category-to-printer-type assignments map.
 */
export const getCategoryAssignments = () => {
  let map = {};
  try {
    const raw = localStorage.getItem("kds_category_assignments") || localStorage.getItem("restro_category_assignments");
    if (raw) {
      map = JSON.parse(raw);
    }
  } catch (e) {
    console.error("Error parsing category assignments:", e);
  }
  return map && typeof map === "object" ? map : {};
};

/**
 * Saves category assignment to a Printer Type.
 */
export const saveCategoryAssignment = (categoryId, printerTypeId) => {
  if (!categoryId) return;
  const assignments = getCategoryAssignments();
  const catKey = String(categoryId);

  if (!printerTypeId || printerTypeId === DEFAULT_PRINTER_TYPE_ID) {
    delete assignments[catKey];
  } else {
    assignments[catKey] = printerTypeId;
  }

  localStorage.setItem("kds_category_assignments", JSON.stringify(assignments));
  localStorage.setItem("restro_category_assignments", JSON.stringify(assignments));
  return assignments;
};

/**
 * Group items by Printer Type ID.
 * Items in categories assigned to different Printer Types are placed into separate groups,
 * even if both Printer Types map to the SAME physical printer.
 */
export const groupItemsByPrinter = (cartItems, categories = []) => {
  const groups = {};
  const printerTypes = getPrinterTypes();
  const defaultType = printerTypes.find(p => p.id === DEFAULT_PRINTER_TYPE_ID) || printerTypes[0];
  const assignments = getCategoryAssignments();
  const defaultPrinter = localStorage.getItem("billPrinter") || "Default";

  // Fallback to cached categories if none provided
  if (!categories || categories.length === 0) {
    try {
      const cached = localStorage.getItem("cached_categories");
      if (cached) {
        categories = JSON.parse(cached);
      } else {
        const bootstrap = localStorage.getItem("restro_pos_bootstrap_v1");
        if (bootstrap) {
          const parsed = JSON.parse(bootstrap);
          if (parsed && Array.isArray(parsed.categories)) {
            categories = parsed.categories;
          }
        }
      }
    } catch(e) {}
  }

  let categoryCopiesMap = {};
  try {
    categoryCopiesMap = JSON.parse(localStorage.getItem("category_kot_copies") || "{}");
  } catch (e) {}

  (cartItems || []).forEach((item) => {
    const categoryId = item.category_id != null ? item.category_id : (item.category != null ? item.category : item.categoryId);
    const categoryTitle = item.category_title || item.categoryTitle || (typeof item.category === 'string' && isNaN(Number(item.category)) ? item.category : null);

    // 1. Identify category from ID or Title
    let matchedCategory = null;
    if (Array.isArray(categories) && categories.length > 0) {
      matchedCategory = categories.find((c) => 
        c && (
          (categoryId != null && String(c.id) === String(categoryId)) ||
          (categoryTitle && c.title && c.title.toLowerCase() === categoryTitle.toLowerCase()) ||
          (categoryId && typeof categoryId === 'string' && c.title && c.title.toLowerCase() === categoryId.toLowerCase())
        )
      );
    }

    let catId = matchedCategory ? matchedCategory.id : (categoryId != null ? categoryId : null);

    // Fallback: If catId is still unknown, look up menu items cache by item_id
    if (!catId && (item.item_id || item.id)) {
      try {
        const menuCache = localStorage.getItem("cached_menu_items") || localStorage.getItem("restro_pos_bootstrap_v1");
        if (menuCache) {
          const parsed = JSON.parse(menuCache);
          const list = Array.isArray(parsed) ? parsed : (parsed?.menuItems || []);
          const found = list.find(m => String(m.id) === String(item.item_id || item.id));
          if (found && (found.category != null || found.category_id != null)) {
            catId = found.category != null ? found.category : found.category_id;
            matchedCategory = categories.find(c => c && String(c.id) === String(catId));
          }
        }
      } catch (e) {}
    }

    // 2. Resolve printer type ID
    let assignedTypeId = null;

    // Check direct category assignment map (by category ID)
    if (catId != null && assignments[String(catId)]) {
      assignedTypeId = assignments[String(catId)];
    } else if (catId != null && assignments[Number(catId)]) {
      assignedTypeId = assignments[Number(catId)];
    }

    // Check if category title is mapped
    if (!assignedTypeId && matchedCategory?.title && assignments[matchedCategory.title]) {
      assignedTypeId = assignments[matchedCategory.title];
    }

    // Check matchedCategory.printer (can be printer type ID or printer name)
    if (!assignedTypeId && matchedCategory && matchedCategory.printer) {
      const matchType = printerTypes.find(pt => String(pt.id) === String(matchedCategory.printer) || pt.name.toLowerCase() === String(matchedCategory.printer).toLowerCase());
      if (matchType) {
        assignedTypeId = matchType.id;
      }
    }

    // Check item.category_printer
    if (!assignedTypeId && item.category_printer) {
      const matchType = printerTypes.find(pt => pt.id === item.category_printer || pt.name.toLowerCase() === item.category_printer.toLowerCase());
      if (matchType) {
        assignedTypeId = matchType.id;
      }
    }

    // Check item-level printer_type_id
    if (!assignedTypeId && item.printer_type_id) {
      assignedTypeId = item.printer_type_id;
    }

    // Check localStorage category_printer_<id>
    if (!assignedTypeId && catId != null) {
      const stored = localStorage.getItem(`category_printer_${catId}`);
      if (stored) {
        const matchType = printerTypes.find(pt => pt.id === stored || pt.name.toLowerCase() === stored.toLowerCase());
        if (matchType) {
          assignedTypeId = matchType.id;
        }
      }
    }

    // Fallback to default printer type
    if (!assignedTypeId) {
      assignedTypeId = DEFAULT_PRINTER_TYPE_ID;
    }

    // Find the resolved PrinterType object
    let targetType = printerTypes.find(pt => pt.id === assignedTypeId) || defaultType;

    // Resolve physical printer destination:
    let physicalPrinterName = targetType.isOsPrinter 
      ? (targetType.osPrinterName || defaultPrinter)
      : (targetType.ipPrinterAddress ? `IP:${targetType.ipPrinterAddress}` : (targetType.osPrinterName || defaultPrinter));

    // Legacy fallback: if matchedCategory had a raw physical printer name and no type matched
    if (assignedTypeId === DEFAULT_PRINTER_TYPE_ID && matchedCategory && matchedCategory.printer && !printerTypes.some(pt => pt.name.toLowerCase() === matchedCategory.printer.toLowerCase())) {
      physicalPrinterName = matchedCategory.printer;
    }

    // Determine copies: printer type copies, or category specific copies, or global printCount
    let copies = targetType.copies || 1;
    if (catId != null && categoryCopiesMap[catId] && parseInt(categoryCopiesMap[catId]) > copies) {
      copies = parseInt(categoryCopiesMap[catId]);
    }

    // Grouping KEY is targetType.id!
    // This guarantees that even if two PrinterTypes point to the SAME physical printer,
    // they are placed into SEPARATE groups and printed as SEPARATE KOT slips!
    const groupKey = targetType.id;

    if (!groups[groupKey]) {
      groups[groupKey] = {
        printerTypeId: targetType.id,
        printerTypeName: targetType.name,
        printerName: physicalPrinterName,
        isOsPrinter: targetType.isOsPrinter,
        ipPrinterAddress: targetType.ipPrinterAddress,
        copies: copies,
        paperFormat: targetType.paperFormat || localStorage.getItem("page_format") || "80",
        items: []
      };
    } else {
      if (copies > groups[groupKey].copies) {
        groups[groupKey].copies = copies;
      }
    }

    groups[groupKey].items.push(item);
  });

  return groups;
};

export const printKOT = async (printerGroups, orderDetails = {}) => {
  const electron = getElectron();
  const isElectron = isElectronEnv();
  const isBluetooth = !!connectedBluetoothDevice;
  const globalPageFormat = orderDetails.pageFormat || orderDetails.page_format || localStorage.getItem('page_format') || '80';

  for (const [groupKey, groupData] of Object.entries(printerGroups)) {
    const items = Array.isArray(groupData) ? groupData : (groupData.items || []);
    if (!items || items.length === 0) continue;

    const printerName = (groupData && groupData.printerName) ? groupData.printerName : groupKey;
    const printerTypeName = (groupData && groupData.printerTypeName) ? groupData.printerTypeName : (orderDetails.printerTypeName || orderDetails.printer_type_name || "KOT");
    const pageFormat = (groupData && groupData.paperFormat) ? groupData.paperFormat : globalPageFormat;
    const groupCopies = (groupData && groupData.copies) ? parseInt(groupData.copies) : (parseInt(localStorage.getItem(`printer_copies_${printerName}`)) || parseInt(localStorage.getItem('printCount')) || 1);
    const totalCopies = Math.max(1, groupCopies);

    console.log(`[PrinterHelper] Printing KOT for [${printerTypeName}] to "${printerName}" (${items.length} items, Copies: ${totalCopies}, Format: ${pageFormat}):`, items);

    // Print each copy with its own label: COPY 1 OF 2, COPY 2 OF 2... (matching Flutter KDS)
    for (let c = 0; c < totalCopies; c++) {
      const copyLabel = totalCopies > 1 ? `COPY ${c + 1} OF ${totalCopies}` : null;
      const copyOrderDetails = {
        ...orderDetails,
        printerTypeName: printerTypeName,
        printer_type_name: printerTypeName,
        copyLabel: copyLabel,
        copy_label: copyLabel,
        copyNumber: c + 1,
        totalCopies: totalCopies
      };

      const html = generateKOTHTML(items, copyOrderDetails, pageFormat);
      const plainText = generateKOTPlainText(items, copyOrderDetails, pageFormat);

      if (isBluetooth) {
        try {
          console.log(`[Web POS] Attempting direct Web Bluetooth printing (${c + 1}/${totalCopies})...`);
          await printViaWebBluetooth(html, plainText);
          console.log(`[Web POS] Web Bluetooth print succeeded.`);
          continue;
        } catch (btErr) {
          console.warn(`[Web POS] Web Bluetooth print failed:`, btErr);
        }
      }

      if (isElectron) {
        try {
          const defaultPrinter = localStorage.getItem("billPrinter") || "";
          const targetPrinter = (printerName && printerName !== "Default") ? printerName : defaultPrinter;

          const payload = {
            printerName: targetPrinter,
            html: html,
            plainText: plainText,
            copies: 1, // each copy sent individually with copyLabel
            pageFormat: pageFormat
          };

          if (electron && electron.ipcRenderer) {
            electron.ipcRenderer.send('print-kot', payload);
          } else if (electron && typeof electron.printKOT === 'function') {
            electron.printKOT(payload);
          } else if (window.require) {
            window.require('electron').ipcRenderer.send('print-kot', payload);
          }
          continue;
        } catch (electronErr) {
          console.warn("Electron silent KOT print failed, falling back to agent:", electronErr);
        }
      }

      // WEB BROWSER: Try WebSocket Print Agent first, fallback to browser print dialog
      try {
        console.log(`[Web POS] Attempting silent WebSocket printing to "${printerName}" for [${printerTypeName}] (${c + 1}/${totalCopies})...`);
        await printViaWebSocketAgent(printerName, html, plainText, pageFormat);
        console.log(`[Web POS] Silent print succeeded for "${printerName}"`);
      } catch (wsErr) {
        console.warn(`[Web POS] WebSocket Print Agent failed or unreachable:`, wsErr.message);
        console.log(`[Web POS] Falling back to standard browser print dialog...`);

        setDetailsForReceiptPrint({
          ...copyOrderDetails,
          cartItems: items
        });

        const receiptWindow = window.open("/print-kot", "_blank", "toolbar=yes,scrollbars=yes,resizable=yes,top=500,left=500,width=400,height=400");
        if (receiptWindow) {
          receiptWindow.onload = () => {
            setTimeout(() => {
              receiptWindow.print();
            }, 800);
          };
        }
      }

      // Small delay between print jobs so spooler/network buffer processes cleanly
      await new Promise(r => setTimeout(r, 300));
    }
  }
};

export const generateKOTHTML = (items, details, pageFormat = '80') => {
  const { tableId, table_title, tokenNo, orderId, customerName, copyLabel } = details || {};
  const waiterName = details.waiterName || details.waiter_name || details.waiter || details.created_by || "";
  
  const isRoom = details.deliveryType === 'room' || details.delivery_type === 'room' || !!details.room_name || !!details.source || (table_title && table_title.toLowerCase().includes('room'));
  const roomName = details.room_name || details.source || (table_title ? table_title.replace(/^room\s*/i, '') : '');
  const diningLabel = isRoom 
    ? `ROOM: ${roomName ? (roomName.toLowerCase().startsWith('room') ? roomName.toUpperCase() : roomName.toUpperCase()) : 'ROOM ORDER'}` 
    : (table_title ? `TABLE: ${table_title.toUpperCase()}` : (details.deliveryType ? details.deliveryType.toUpperCase() : "TAKEAWAY / DELIVERY"));

  const is58mm = String(pageFormat).includes('58') || pageFormat === '58' || Number(pageFormat) <= 60;
  const paperWidthCss = is58mm ? '48mm' : '72mm';
  const bodyPadding = is58mm ? '1mm' : '2mm 1mm';
  const titleSize = is58mm ? '1.25rem' : '1.55rem';
  const diningBoxSize = is58mm ? '1.2rem' : '1.45rem';
  const itemTitleSize = is58mm ? '1.15rem' : '1.35rem';
  const qtyBoxSize = is58mm ? '1.3rem' : '1.6rem';
  const waiterBoxSize = is58mm ? '1.05rem' : '1.2rem';

  const headerTitle = details?.printer_type_name || details?.printerTypeName || "KITCHEN ORDER (KOT)";

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <style>
          @page { 
            size: auto; 
            margin: 0 !important; 
          }
          * { 
            box-sizing: border-box; 
          }
          html, body { 
            font-family: 'Courier New', Courier, monospace; 
            width: 100% !important;
            margin: 0 !important; 
            padding: 0 !important; 
            color: #000; 
            background: #fff;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .receipt-wrapper {
            width: 100% !important;
            max-width: ${paperWidthCss} !important;
            margin: 0 auto !important;
            padding: ${bodyPadding} !important;
            box-sizing: border-box !important;
          }
          .header { text-align: center; border-bottom: 2.5px solid #000; padding-bottom: 5px; margin-bottom: 6px; }
          .title { font-size: ${titleSize}; font-weight: 900; letter-spacing: 0.5px; }
          .copy-banner { border: 2px solid #000; padding: 2px 4px; margin: 3px 0; font-weight: 900; font-size: 1rem; text-align: center; }
          .token-table { font-size: 1.15rem; font-weight: 900; margin: 4px 0; }
          .dining-box { border: 2.5px solid #000; padding: 4px 6px; margin: 5px 0; font-weight: 900; font-size: ${diningBoxSize}; text-align: center; background: #fff; letter-spacing: 0.5px; }
          .waiter-box { border: 2px solid #000; padding: 3px 5px; margin: 4px 0; font-weight: 900; font-size: ${waiterBoxSize}; text-align: center; }
          .item-container { margin-bottom: 6px; border-bottom: 1.5px dashed #000; padding-bottom: 5px; }
          .item { display: flex; align-items: flex-start; gap: 8px; }
          .item-qty-box { font-size: ${qtyBoxSize}; font-weight: 900; border: 2.5px solid #000; padding: 2px 6px; border-radius: 4px; min-width: 32px; text-align: center; line-height: 1.1; }
          .item-content { flex: 1; min-width: 0; }
          .item-title { font-weight: 900; font-size: ${itemTitleSize}; line-height: 1.15; word-break: break-word; }
          .variant-title { font-weight: 900; font-size: 1.05rem; margin-top: 2px; }
          .addons { font-size: 0.95rem; font-weight: bold; margin-top: 3px; }
          .notes { font-size: 0.95rem; font-weight: 900; margin-top: 3px; background: #f0f0f0; padding: 2px 4px; border-left: 3px solid #000; }
          .footer { border-top: 2.5px solid #000; margin-top: 8px; padding-top: 5px; text-align: center; font-size: 0.95rem; font-weight: 900; }
        </style>
      </head>
      <body>
        <div class="receipt-wrapper">
          <div class="header">
            <div class="title">*** ${headerTitle.toUpperCase()} ***</div>
            ${copyLabel ? `<div class="copy-banner">*** ${copyLabel} ***</div>` : ''}
            <div class="token-table">TOKEN: ${tokenNo || "-"} | ORDER #${orderId || ""}</div>
            <div class="dining-box">${diningLabel}</div>
            ${waiterName ? `<div class="waiter-box">WAITER: ${waiterName.toUpperCase()}</div>` : ""}
            <div style="font-size: 0.85rem; font-weight: bold; color: #000; margin-top: 3px;">DATE/TIME: ${new Date().toLocaleString()}</div>
          </div>
          <div class="items">
            ${items.map(item => {
              const vTitle = item.variant_title || item.variant?.title || (typeof item.variant === 'string' ? item.variant : '') || '';
              const itemTitle = item.title || item.item_title || '';
              const qty = item.quantity ?? item.qty ?? 1;
              return `
              <div class="item-container">
                <div class="item">
                  <span class="item-qty-box">${qty}</span>
                  <div class="item-content">
                    <div class="item-title">${itemTitle}</div>
                    ${vTitle ? `<div class="variant-title">&gt;&gt; ${vTitle}</div>` : ''}
                    ${item.addons?.length > 0 ? `<div class="addons">+ ADDONS: ${item.addons.map(a => typeof a === 'object' ? (a.title || a.name || '') : String(a)).filter(Boolean).join(", ")}</div>` : ""}
                    ${item.notes ? `<div class="notes">*** NOTE: ${item.notes} ***</div>` : ""}
                  </div>
                </div>
              </div>
              `;
            }).join("")}
          </div>
          <div class="footer">
            --- END OF ${headerTitle.toUpperCase()} ---
          </div>
          <!-- Clearance feed so thermal paper cutter doesn't cut through text -->
          <div style="height: 10mm; clear: both;"></div>
        </div>
      </body>
    </html>
  `;
};

export const generateReceiptHTML = (details, pageFormat = '58') => {
  details.cartItems = __groupReceiptItems(details.cartItems);
  const selectedFormat = localStorage.getItem('bill_format') || details.bill_format || details.billFormat || details.printSettings?.bill_format || details.printSettings?.billFormat || 'retail_tax_invoice';

  const { 
    cartItems = [], deliveryType = '', customerType = '', customer = null, 
    currency = 'Rs.', storeSettings = {}, printSettings = {},
    itemsTotal = 0, taxTotal = 0, serviceChargeTotal = 0, payableTotal = 0,
    storedDiscount = 0, roundOff = 0, paidAmount = undefined, dueAmount = 0,
    tokenNo = '', orderId = '', paymentMethod = '', multiPayments = [],
    table_title = '', isEstimate = false
  } = details || {};

  const is58mm = String(pageFormat).includes('58') || pageFormat === '58' || Number(pageFormat) <= 60;
  const paperWidthCss = is58mm ? '48mm' : '72mm';
  const bodyPadding = is58mm ? '1mm' : '2mm 1mm';

  const storeName = storeSettings?.store_name || details?.store_name || 'RESTAURANT';
  const address = storeSettings?.address || details?.address || '';
  const phone = storeSettings?.phone || details?.phone || '';
  const panNo = storeSettings?.pan_no || storeSettings?.panNo || details?.pan_no || '';
  const isVat = Boolean(storeSettings?.is_vat_registered || storeSettings?.isVatRegistered);
  const headerText = printSettings?.header || details?.header || '';
  const footerText = printSettings?.footer || details?.footer || 'Thank You! Visit Again';
  
  const rawCustomerName = customer?.name || details?.customerName || details?.customer_name || details?.guest_name || '';
  const rawCustomerId = customer?.id || details?.customerId || details?.customer_id || null;
  const rawCustomerType = customerType || details?.customer_type || '';

  const hasCustomer = Boolean(rawCustomerId && String(rawCustomerId).trim().toUpperCase() !== 'WALK-IN' && String(rawCustomerId).trim().toUpperCase() !== 'WALKIN');
  const isWalkinName = !rawCustomerName || ['walkin', 'walk-in', 'walk-in guest', 'walk-in customer', 'walk in', 'cash customer', 'guest'].includes(rawCustomerName.trim().toLowerCase());
  const isWalkinType = ['walkin', 'walk-in'].includes(String(rawCustomerType).trim().toLowerCase());

  const isWalkin = (!hasCustomer && isWalkinName) || isWalkinType;
  const displayCustomerType = isWalkin ? 'Walk-in' : (rawCustomerType && !['walkin', 'walk-in'].includes(String(rawCustomerType).trim().toLowerCase()) ? (String(rawCustomerType).trim().toLowerCase() === 'room' ? 'Room Guest' : (String(rawCustomerType).trim().toUpperCase() === 'CUSTOMER' ? 'Customer' : rawCustomerType)) : 'Customer');
  const displayCustomerName = isWalkin ? 'Walk-in' : (rawCustomerName || 'Walk-in');
  const buyerPan = customer?.pan_no || details?.buyerPan || details?.buyer_pan || '';

  const curSymbol = typeof currency === 'object' && currency?.symbol ? currency.symbol : (currency || 'Rs.');
  const fmt = (v) => `${curSymbol} ${Number(v || 0).toFixed(2)}`;

  const now = new Date();
  const bsDate = getNepaliDateBS(details.billedTime || details.date || now);
  const fiscalYear = getNepaliFiscalYear(details.billedTime || details.date || now);
  const formattedDateTime = formatReceiptDateTime(details.billedTime || details.date || now);
  const areaName = details.area || (deliveryType === 'takeaway' ? 'TAKEAWAY' : (deliveryType === 'delivery' ? 'DELIVERY' : 'HALL'));
  const cleanTitle = (details.table_title || details.tableName || details.room_name || details.source || '').trim();

  const subTotalNum = Number(String(itemsTotal || 0).replace(/,/g, ''));
  const discountNum = Number(String(storedDiscount || 0).replace(/,/g, ''));
  const scNum = Number(String(serviceChargeTotal || 0).replace(/,/g, ''));
  const taxableAmt = details.taxableAmount !== undefined && details.taxableAmount !== null
    ? Number(String(details.taxableAmount).replace(/,/g, ''))
    : Math.max(0, subTotalNum - discountNum + scNum);
  const taxNum = Number(String(taxTotal || 0).replace(/,/g, ''));
  const roundOffNum = Number(String(roundOff || 0).replace(/,/g, ''));
  const grandTotalNum = Number(String(payableTotal || 0).replace(/,/g, ''));
  const discountPercent = subTotalNum > 0 && discountNum > 0 ? Math.round((discountNum / subTotalNum) * 100) : 0;
  const wordsOnly = numberToWords(grandTotalNum, "").replace(/Rupees/gi, "").trim();

  // 1. NEPAL RETAIL TAX INVOICE FORMAT
  if (selectedFormat === 'retail_tax_invoice') {
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${isEstimate ? 'Estimate Bill' : 'Tax Invoice'} - ${details.billNo || orderId}</title>
  <style>
    @page { size: ${is58mm ? '58mm' : '80mm'} auto; margin: 0; }
    * { box-sizing: border-box; }
    body {
      font-family: 'Consolas', 'Courier New', Courier, Monaco, monospace;
      width: ${paperWidthCss};
      max-width: ${paperWidthCss};
      margin: 0 auto;
      padding: ${bodyPadding};
      font-size: ${is58mm ? '9.5px' : '11px'};
      line-height: ${is58mm ? '1.25' : '1.35'};
      color: #000;
      background: #fff;
      -webkit-font-smoothing: antialiased;
      overflow: hidden;
    }
    .text-center { text-align: center; }
    .text-right { text-align: right; }
    .store-name { font-size: ${is58mm ? '14px' : '17px'}; font-weight: 900; line-height: 1.15; }
    .sub-header { font-size: ${is58mm ? '10px' : '12px'}; font-weight: 500; margin-top: 1px; }
    .doc-title { font-size: ${is58mm ? '11px' : '13px'}; font-weight: 700; margin-top: 3px; text-transform: uppercase; }
    .divider { border-top: 1px solid #000; margin: 4px 0; }
    table { width: 100%; border-collapse: collapse; margin: 2px 0; table-layout: fixed; }
    th { border-bottom: 1px solid #000; padding: 2px 0; font-size: ${is58mm ? '9px' : '10.5px'}; font-weight: 700; }
    td { padding: 1.5px 0; font-size: ${is58mm ? '9px' : '10.5px'}; vertical-align: top; }
    @media print {
      .no-print { display: none !important; }
      html, body { background: #fff !important; }
    }
  </style>
</head>
<body>
  <div class="text-center">
    <div class="store-name">${storeName}</div>
    <div class="sub-header">${address}</div>
    ${phone ? `<div class="sub-header">Tel: ${phone}</div>` : ""}
    <div class="doc-title">${isEstimate ? 'Estimate Bill' : 'Tax Invoice'}</div>
  </div>

  <div style="margin-top: 6px; font-size: ${is58mm ? '9px' : '10.5px'}; line-height: 1.3;">
    <div style="display: flex; justify-content: space-between;">
      <span>${panNo ? `PAN: ${panNo}` : ""}</span>
      <span>${buyerPan ? `Buyer PAN: ${buyerPan}` : ""}</span>
    </div>
    <div style="display: flex; justify-content: space-between;"><span style="font-weight: 600;">Bill No:</span><span>${details.billNo || orderId}</span></div>
    <div style="display: flex; justify-content: space-between;"><span style="font-weight: 600;">Area:</span><span>${areaName}</span></div>
    ${cleanTitle ? `<div style="display: flex; justify-content: space-between;"><span style="font-weight: 600;">Room/Table:</span><span style="font-weight: 700;">${cleanTitle}</span></div>` : ''}
    <div style="display: flex; justify-content: space-between;"><span style="font-weight: 600;">Customer Type:</span><span>${displayCustomerType}</span></div>
    <div style="display: flex; justify-content: space-between;"><span style="font-weight: 600;">Customer:</span><span style="font-weight: 700;">${displayCustomerName}</span></div>
    ${customer?.phone ? `<div style="display: flex; justify-content: space-between;"><span style="font-weight: 600;">Contact:</span><span>${customer.phone}</span></div>` : ''}
    <div style="display: flex; justify-content: space-between;"><span style="font-weight: 600;">Date:</span><span>${formattedDateTime}</span></div>
    <div style="display: flex; justify-content: space-between;"><span style="font-weight: 600;">Miti:</span><span>${bsDate}</span></div>
    <div style="display: flex; justify-content: space-between;"><span style="font-weight: 600;">Fiscal Year:</span><span>${fiscalYear}</span></div>
  </div>

  <div class="divider"></div>

  <table>
    <thead>
      <tr>
        <th style="text-align: left; width: 44%;">Item</th>
        <th style="text-align: right; width: 14%;">QTY</th>
        <th style="text-align: right; width: 20%;">Price</th>
        <th style="text-align: right; width: 22%;">Total</th>
      </tr>
    </thead>
    <tbody>
      ${cartItems.map(it => {
        const qty = Number(it.quantity || it.qty || 1);
        const rate = Number(it.price || it.rate || 0);
        const tot = qty * rate;
        const vTitle = it.variant_title || it.variant?.title || '';
        return `
        <tr>
          <td style="text-align: left; word-break: break-word;">${it.title || it.item_title}${vTitle ? ` (${vTitle})` : ''}</td>
          <td style="text-align: right; white-space: nowrap;">${qty}</td>
          <td style="text-align: right; white-space: nowrap;">${rate.toFixed(2)}</td>
          <td style="text-align: right; white-space: nowrap;">${tot.toFixed(2)}</td>
        </tr>
        `;
      }).join("")}
    </tbody>
  </table>

  <div class="divider"></div>

  <div style="margin: 3px 0; font-size: ${is58mm ? '9.5px' : '10.5px'}; line-height: 1.35;">
    <div style="display: flex; justify-content: space-between;"><span>Sub-Total :</span><span>${subTotalNum.toFixed(2)}</span></div>
    ${discountNum > 0 ? `<div style="display: flex; justify-content: space-between;"><span>Discount ${discountPercent > 0 ? `(${discountPercent}%)` : ''} :</span><span>-${discountNum.toFixed(2)}</span></div>` : ''}
    ${scNum > 0 ? `<div style="display: flex; justify-content: space-between;"><span>Service Charge :</span><span>+${scNum.toFixed(2)}</span></div>` : ''}
    <div style="display: flex; justify-content: space-between; font-weight: 600;"><span>${(isVat || taxNum > 0) ? 'Taxable AMT :' : 'NonTaxable AMT :'}</span><span>${taxableAmt.toFixed(2)}</span></div>
    ${taxNum > 0 ? `<div style="display: flex; justify-content: space-between;"><span>13% VAT :</span><span>+${taxNum.toFixed(2)}</span></div>` : ''}
    ${roundOffNum !== 0 ? `<div style="display: flex; justify-content: space-between;"><span>Round Off :</span><span>${roundOffNum > 0 ? `+` : ``}${roundOffNum.toFixed(2)}</span></div>` : ''}
  </div>

  <div style="border-top: 1px solid #000; border-bottom: 1px solid #000; padding: 3px 0; margin: 4px 0; display: flex; justify-content: space-between; font-size: ${is58mm ? '13px' : '15px'}; font-weight: 900;">
    <span>Grand Total:</span><span>${grandTotalNum.toFixed(2)}</span>
  </div>

  <div style="font-size: ${is58mm ? '9px' : '10.5px'}; margin-top: 3px; line-height: 1.3;">
    <div style="word-break: break-word;">In Words: ${wordsOnly} Only</div>
    <div style="display: flex; justify-content: space-between; margin-top: 2px;">
      <span>Mode: ${paymentMethod || 'Cash'}</span>
      <span>Received: ${Number(paidAmount !== undefined ? paidAmount : grandTotalNum).toFixed(2)}</span>
    </div>
    ${Number(dueAmount || 0) > 0 ? `<div style="display: flex; justify-content: space-between; font-weight: 700;"><span>Balance Due:</span><span>${Number(dueAmount).toFixed(2)}</span></div>` : ''}
    ${(details.notes || details.note) ? `<div style="margin-top: 3px; font-style: italic;">Note: ${details.notes || details.note}</div>` : ''}
  </div>

  <div class="text-center" style="font-size: ${is58mm ? '11px' : '13px'}; font-weight: 700; margin: 8px 0;">
    Thank You.
  </div>

  <div style="display: flex; justify-content: space-between; font-size: ${is58mm ? '9px' : '10px'}; margin-top: 10px; padding: 0 2px;">
    <div><div style="width: 60px; border-top: 1px solid #000; margin-bottom: 2px;"></div><div>Cashier</div></div>
    <div style="text-align: right;"><div style="width: 60px; border-top: 1px solid #000; margin-bottom: 2px; margin-left: auto;"></div><div>Customer</div></div>
  </div>

  <div class="text-center" style="font-size: ${is58mm ? '8.5px' : '9.5px'}; margin-top: 8px; color: #333;">
    <div>Powered By : STJ Technologies</div>
    <div>www.ehospitalitynepal.com</div>
  </div>
</body>
</html>`;
  }

  // 2. STANDARD / POS RECEIPT FORMAT FALLBACK
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${isEstimate ? 'Estimate Bill' : 'Receipt'}</title>
  <style>
    @page { size: auto; margin: 2mm 3mm !important; }
    * { box-sizing: border-box; }
    html, body { 
      font-family: 'Courier New', Courier, monospace; 
      width: 100% !important; margin: 0 !important; padding: 0 !important; 
      color: #000 !important; background: #fff !important; 
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }
    .receipt-wrapper {
      width: 100% !important; max-width: ${paperWidthCss} !important;
      margin: 0 auto !important; padding: ${bodyPadding} !important;
    }
    .center { text-align: center; }
    .bold { font-weight: 900; }
    .store-name { font-size: ${is58mm ? '1.1rem' : '1.35rem'}; font-weight: 900; text-transform: uppercase; margin-bottom: 2px; }
    .sub-text { font-size: ${is58mm ? '0.8rem' : '0.9rem'}; line-height: 1.2; }
    .divider { border-bottom: 1px dashed #000; margin: 4px 0; }
    .double-divider { border-bottom: 2px solid #000; margin: 5px 0; }
    .banner { border: 2px solid #000; padding: 2px 4px; margin: 4px 0; font-weight: 900; text-align: center; }
    table.items { width: 100%; border-collapse: collapse; font-size: ${is58mm ? '0.75rem' : '0.85rem'}; }
    table.items th { border-bottom: 1px solid #000; padding: 2px 0; text-align: left; }
    table.items td { padding: 2px 0; vertical-align: top; }
    .text-right { text-align: right; }
    .summary-row { display: flex; justify-content: space-between; font-size: ${is58mm ? '0.8rem' : '0.9rem'}; padding: 1px 0; }
    .grand-total { display: flex; justify-content: space-between; font-size: 1.1rem; font-weight: 900; border-top: 1.5px solid #000; border-bottom: 1.5px solid #000; padding: 3px 0; margin: 4px 0; }
    .cutter-space { height: 8mm; clear: both; }
    @media print { .no-print { display: none !important; } html, body { background: #fff !important; } }
  </style>
</head>
<body>
  <div class="receipt-wrapper">
    <div class="center">
      <div class="store-name">${storeName}</div>
      ${address ? `<div class="sub-text">${address}</div>` : ''}
      ${phone ? `<div class="sub-text">Tel: ${phone}</div>` : ''}
      ${panNo ? `<div class="sub-text bold">${isVat ? 'VAT' : 'PAN'}: ${panNo}</div>` : ''}
      ${headerText ? `<div class="sub-text" style="margin-top:2px;">${headerText}</div>` : ''}
    </div>

    <div class="banner">
      <div style="font-size: 0.95rem;">${isEstimate ? 'PROFORMA ESTIMATE' : (isVat ? 'TAX INVOICE' : 'INVOICE')}</div>
      <div style="font-size: 0.72rem;">${isEstimate ? '(अनुमानित बिल - NOT A TAX INVOICE)' : '(कर बिजक)'}</div>
    </div>

    <div class="sub-text" style="margin-top: 4px;">
      ${orderId ? `<div><b>Order #:</b> ${orderId}</div>` : ''}
      ${details.billNo ? `<div><b>Bill No:</b> ${details.billNo}</div>` : ''}
      ${tokenNo ? `<div><b>Token #:</b> ${tokenNo}</div>` : ''}
      ${cleanTitle ? `<div><b>Room/Table:</b> ${cleanTitle}</div>` : ''}
      <div><b>Customer Type:</b> ${displayCustomerType}</div>
      <div><b>Customer:</b> ${displayCustomerName}</div>
      ${buyerPan ? `<div><b>Buyer PAN:</b> ${buyerPan}</div>` : ''}
      ${customer?.phone ? `<div><b>Phone:</b> ${customer.phone}</div>` : ''}
      <div><b>Billed:</b> ${formattedDateTime}</div>
    </div>

    <div class="divider"></div>

    <table class="items">
      <thead>
        <tr>
          <th style="width: 15%;">Qty</th>
          <th style="width: 55%;">Item</th>
          <th style="width: 30%; text-align: right;">Total</th>
        </tr>
      </thead>
      <tbody>
        ${cartItems.map(item => {
          const qty = item.quantity || item.qty || 1;
          const title = item.title || item.item_title || '';
          const variant = item.variant_title || item.variant?.title || '';
          const rate = Number(item.price || item.rate || 0);
          return `
            <tr>
              <td>${qty}</td>
              <td><b>${title}</b> ${variant ? `(${variant})` : ''}</td>
              <td class="text-right">${fmt(rate * qty)}</td>
            </tr>
          `;
        }).join('')}
      </tbody>
    </table>

    <div class="divider"></div>

    <div class="summary-row"><span>Subtotal:</span><span>${fmt(itemsTotal)}</span></div>
    ${Number(storedDiscount || 0) > 0 ? `<div class="summary-row"><span>Discount:</span><span>-${fmt(storedDiscount)}</span></div>` : ''}
    ${Number(serviceChargeTotal || 0) > 0 ? `<div class="summary-row"><span>Service Chg:</span><span>+${fmt(serviceChargeTotal)}</span></div>` : ''}
    ${(isVat || Number(taxTotal || 0) > 0) ? `<div class="summary-row"><span>Taxable Amount:</span><span>${fmt(taxableAmt)}</span></div>` : ''}
    ${Number(taxTotal || 0) > 0 ? `<div class="summary-row"><span>${isVat ? '13% VAT' : 'Tax'}:</span><span>+${fmt(taxTotal)}</span></div>` : ''}
    ${Number(roundOff || 0) !== 0 ? `<div class="summary-row"><span>Round Off:</span><span>${Number(roundOff) > 0 ? '+' : ''}${fmt(roundOff)}</span></div>` : ''}

    <div class="grand-total">
      <span>${isEstimate ? 'EST. TOTAL:' : 'NET TOTAL:'}</span>
      <span>${fmt(payableTotal)}</span>
    </div>

    ${paymentMethod ? `<div class="summary-row bold"><span>Payment Mode:</span><span>${paymentMethod}</span></div>` : ''}
    ${Number(paidAmount || 0) > 0 ? `<div class="summary-row bold"><span>Total Paid:</span><span>${fmt(paidAmount)}</span></div>` : ''}
    ${Number(dueAmount || 0) > 0 ? `<div class="summary-row bold" style="color:#d9534f;"><span>Due Balance:</span><span>${fmt(dueAmount)}</span></div>` : ''}
    ${(details.notes || details.note) ? `<div style="margin-top: 4px; padding: 2px 4px; font-size: 0.72rem; border-left: 2px solid #000; font-style: italic;"><b>Note:</b> ${details.notes || details.note}</div>` : ''}

    <div class="double-divider"></div>
    <div class="center sub-text" style="margin-top: 4px;">
      <div>${footerText}</div>
      <div style="font-size: 0.7rem; margin-top: 2px;">RestroPro POS • eHospitality</div>
    </div>
    <div class="cutter-space"></div>
  </div>
</body>
</html>`;
};

export const printReceipt = async (details) => {
  const electron = getElectron();
  const isElectron = isElectronEnv();
  const isBluetooth = !!connectedBluetoothDevice;
  const billPrinter = localStorage.getItem("billPrinter") || "Default";
  const pageFormat = details.pageFormat || details.page_format || details.printSettings?.page_format || localStorage.getItem('page_format') || '80';
  const copies = details.copies || details.printSettings?.print_count || 1;

  // Use passed invoice number / bill number if available to match Invoices page, otherwise fallback to session counter
  details.billNo = details.billNo || details.invoice_id || details.invoiceId || details.invoiceNo || details.invoice_no || getNextBillNumber();

  const html = generateReceiptHTML(details, pageFormat);
  const plainText = generateReceiptPlainText(details, pageFormat);

  if (isBluetooth) {
    try {
      for (let c = 0; c < copies; c++) {
        await printViaWebBluetooth(html, plainText);
      }
      return;
    } catch (btErr) {
      console.warn("Bluetooth receipt print failed:", btErr);
    }
  }

  if (isElectron) {
    try {
      if (electron && electron.ipcRenderer) {
        electron.ipcRenderer.send('print-receipt', {
          printerName: billPrinter,
          html: html,
          plainText: plainText,
          copies: copies,
          pageFormat: pageFormat
        });
        return;
      } else if (window.require) {
        window.require('electron').ipcRenderer.send('print-receipt', {
          printerName: billPrinter,
          html: html,
          plainText: plainText,
          copies: copies,
          pageFormat: pageFormat
        });
        return;
      }
    } catch (elErr) {
      console.warn("Electron silent receipt print failed, falling back to agent:", elErr);
    }
  }

  // Web Browser fallback
  try {
    for (let c = 0; c < copies; c++) {
      await printViaWebSocketAgent(billPrinter, html, plainText, pageFormat);
    }
  } catch (wsErr) {
    console.warn("WebSocket agent unavailable, using direct offline HTML print window:", wsErr);
    setDetailsForReceiptPrint(details);

    let printed = false;
    try {
      const printWin = window.open("", "_blank", "toolbar=no,scrollbars=yes,resizable=yes,top=80,left=80,width=480,height=720");
      if (printWin) {
        printWin.document.open();
        printWin.document.write(html);
        printWin.document.close();
        printWin.focus();
        setTimeout(() => {
          try {
            printWin.print();
          } catch (e) {
            console.warn("Popup print trigger error:", e);
          }
        }, 300);
        printed = true;
      }
    } catch (popupErr) {
      console.warn("Popup open blocked or failed, using iframe fallback:", popupErr);
    }

    if (!printed) {
      let iframe = document.getElementById('receipt-print-iframe');
      if (!iframe) {
        iframe = document.createElement('iframe');
        iframe.id = 'receipt-print-iframe';
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        document.body.appendChild(iframe);
      }
      try {
        iframe.contentDocument.open();
        iframe.contentDocument.write(html);
        iframe.contentDocument.close();
        setTimeout(() => {
          try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          } catch (e) {
            console.warn("Iframe print trigger error:", e);
          }
        }, 300);
      } catch (ifErr) {
        console.error("Iframe write failed:", ifErr);
      }
    }
  }
};

// Network Printer Local Storage helpers
export const getNetworkPrinters = () => {
  try {
    const raw = localStorage.getItem('network_printers');
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
};

export const saveNetworkPrinter = (printer) => {
  try {
    const list = getNetworkPrinters();
    const existingIndex = list.findIndex(p => p.id === printer.id || (p.ip === printer.ip && p.port === printer.port));
    if (existingIndex >= 0) {
      list[existingIndex] = { ...list[existingIndex], ...printer };
    } else {
      list.push(printer);
    }
    localStorage.setItem('network_printers', JSON.stringify(list));
    return list;
  } catch (e) {
    console.error('Failed to save network printer:', e);
    return [];
  }
};

export const deleteNetworkPrinter = (printerId) => {
  try {
    const list = getNetworkPrinters();
    const updated = list.filter(p => p.id !== printerId);
    localStorage.setItem('network_printers', JSON.stringify(updated));
    return updated;
  } catch (e) {
    console.error('Failed to delete network printer:', e);
    return [];
  }
};

// Universal Test Print function
export const testPrinter = async (printerInfo) => {
  const isElectron = isElectronEnv();
  const electron = getElectron();
  const { name, isIp, ip, port, format = '80' } = printerInfo;

  if (isElectron) {
    try {
      let result;
      if (electron && electron.testPrint) {
        result = await electron.testPrint({ printerName: name, isIp, ip, port, pageFormat: format });
      } else if (electron && electron.ipcRenderer) {
        result = await electron.ipcRenderer.invoke('test-print', { printerName: name, isIp, ip, port, pageFormat: format });
      } else if (window.require) {
        result = await window.require('electron').ipcRenderer.invoke('test-print', { printerName: name, isIp, ip, port, pageFormat: format });
      }
      return result || { success: true, message: 'Test print sent!' };
    } catch (err) {
      throw new Error(err.message || 'Electron test print failed');
    }
  }

  // Fallback to WebSocket agent
  const wsUrl = localStorage.getItem("printer_agent_url") || "ws://127.0.0.1:8124";
  return new Promise((resolve, reject) => {
    try {
      const ws = new WebSocket(wsUrl);
      const timer = setTimeout(() => {
        ws.close();
        reject(new Error("Connection timed out"));
      }, 3000);

      ws.onopen = () => {
        clearTimeout(timer);
        const targetName = isIp ? `IP:${ip}:${port}` : name;
        const testText = "================================\n       TEST PRINT SUCCESSFUL    \n================================\n" +
          `Printer: ${name || targetName}\nTime: ${new Date().toLocaleString()}\n` +
          "================================\n\n\n";
        ws.send(JSON.stringify({
          printerName: targetName,
          text: testText,
          html: `<div style="text-align: center; font-family: monospace; padding: 10px;"><h2>*** RESTROPRO TEST PRINT ***</h2><p><b>Printer:</b> ${name || targetName}</p><p><b>Status:</b> Ready for KOT & Bill Printing</p><p>${new Date().toLocaleString()}</p></div>`,
          pageFormat: format || '80'
        }));
      };

      ws.onmessage = (event) => {
        try {
          const res = JSON.parse(event.data);
          if (res.success) {
            resolve({ success: true, message: res.message || 'Test print sent' });
          } else {
            reject(new Error(res.message || 'Test print failed'));
          }
        } catch (e) {
          reject(e);
        } finally {
          ws.close();
        }
      };

      ws.onerror = (err) => {
        clearTimeout(timer);
        reject(new Error("WebSocket printer connection failed"));
      };
    } catch (e) {
      reject(e);
    }
  });
};

// Generate Payment Voucher Thermal HTML
export const generatePaymentVoucherHTML = (details, pageFormat = '58') => {
  const {
    voucherNo = '',
    title = 'PAYMENT RECEIPT',
    date = new Date(),
    partyName = 'Customer',
    partyPhone = '',
    partyPan = '',
    partyType = 'Customer',
    invoicesSettled = [],
    paymentMethods = [],
    totalAmount = 0,
    remainingDue = 0,
    notes = '',
    storeSettings = {},
    currency = 'Rs.',
    cashier = ''
  } = details || {};

  const is58mm = String(pageFormat).includes('58') || pageFormat === '58' || Number(pageFormat) <= 60;
  const paperWidthCss = is58mm ? '48mm' : '72mm';
  const titleSize = is58mm ? '1.05rem' : '1.3rem';
  const textSize = is58mm ? '0.78rem' : '0.88rem';

  const storeName = storeSettings?.store_name || 'RESTAURANT';
  const address = storeSettings?.address || '';
  const phone = storeSettings?.phone || '';
  const panNo = storeSettings?.pan_no || storeSettings?.panNo || '';
  const curSymbol = typeof currency === 'object' && currency?.symbol ? currency.symbol : (currency || 'Rs.');
  const fmt = (v) => `${curSymbol} ${Number(v || 0).toFixed(2)}`;

  const formattedDate = date ? new Date(date).toLocaleString() : new Date().toLocaleString();

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <style>
          @page { size: auto; margin: 2mm 3mm !important; }
          * { box-sizing: border-box; }
          html, body {
            font-family: 'Courier New', Courier, monospace;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            color: #000;
            background: #fff;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .receipt-wrapper {
            width: 100% !important;
            max-width: ${paperWidthCss} !important;
            margin: 0 auto !important;
            padding: ${is58mm ? '2mm 3mm' : '3mm 4mm'} !important;
          }
          .center { text-align: center; }
          .bold { font-weight: 900; }
          .store-name { font-size: ${titleSize}; font-weight: 900; text-transform: uppercase; margin-bottom: 2px; }
          .sub-text { font-size: ${textSize}; line-height: 1.2; }
          .divider { border-bottom: 1px dashed #000; margin: 4px 0; }
          .double-divider { border-bottom: 2px solid #000; margin: 5px 0; }
          .banner { border: 2px solid #000; padding: 3px 4px; margin: 5px 0; font-weight: 900; text-align: center; font-size: 0.95rem; }
          .row { display: flex; justify-content: space-between; font-size: ${textSize}; padding: 1.5px 0; }
          .highlight-row { display: flex; justify-content: space-between; font-size: 1rem; font-weight: 900; border-top: 1.5px solid #000; border-bottom: 1.5px solid #000; padding: 3px 0; margin: 4px 0; }
          .due-row { display: flex; justify-content: space-between; font-size: 0.95rem; font-weight: 900; padding: 2px 0; color: #000; }
          .cutter-space { height: 8mm; clear: both; }
        </style>
      </head>
      <body>
        <div class="receipt-wrapper">
          <div class="center">
            <div class="store-name">${storeName}</div>
            ${address ? `<div class="sub-text">${address}</div>` : ''}
            ${phone ? `<div class="sub-text">Tel: ${phone}</div>` : ''}
            ${panNo ? `<div class="sub-text bold">PAN / VAT: ${panNo}</div>` : ''}
          </div>

          <div class="banner">${title.toUpperCase()}</div>

          <div class="row">
            <span>Voucher No:</span>
            <span class="bold">${voucherNo || ('VCH-' + Date.now().toString().slice(-6))}</span>
          </div>
          <div class="row">
            <span>Date & Time:</span>
            <span>${formattedDate}</span>
          </div>
          <div class="row">
            <span>${partyType}:</span>
            <span class="bold">${partyName}</span>
          </div>
          ${partyPhone ? `<div class="row"><span>Phone:</span><span>${partyPhone}</span></div>` : ''}
          ${partyPan ? `<div class="row"><span>PAN:</span><span>${partyPan}</span></div>` : ''}

          <div class="divider"></div>

          ${invoicesSettled && invoicesSettled.length > 0 ? `
            <div style="font-size: ${textSize}; font-weight: bold; margin-bottom: 2px;">${partyType === 'Supplier' ? 'SETTLED BILLS:' : 'SETTLED INVOICES:'}</div>
            ${invoicesSettled.map(inv => `
              <div class="row" style="font-size: 0.75rem;">
                <span>#${inv.invoice_no || inv.purchase_invoice_no || inv.invoice_id || inv.purchase_id || inv.ref_no || '-'}</span>
                <span>${inv.original_due !== undefined ? `Due: ${fmt(inv.original_due)} | ` : ''}Cleared: <b>${fmt(inv.allocated || inv.amount_applied || inv.amount)}</b></span>
              </div>
            `).join('')}
            <div class="divider"></div>
          ` : ''}

          <div style="font-size: ${textSize}; font-weight: bold; margin-bottom: 2px;">PAYMENT DETAILS:</div>
          ${paymentMethods && paymentMethods.length > 0 ? paymentMethods.map(pm => `
            <div class="row">
              <span>${pm.method || pm.title || 'Payment'}:</span>
              <span class="bold">${fmt(pm.amount)}</span>
            </div>
          `).join('') : `
            <div class="row">
              <span>Payment Mode:</span>
              <span class="bold">${details.paymentMethod || 'Cash'}</span>
            </div>
          `}

          <div class="highlight-row">
            <span>${partyType === 'Supplier' ? 'TOTAL PAID:' : 'TOTAL RECEIVED:'}</span>
            <span>${fmt(totalAmount)}</span>
          </div>

          <div class="due-row">
            <span>REMAINING DUE:</span>
            <span>${fmt(remainingDue)}</span>
          </div>

          ${notes ? `
            <div class="divider"></div>
            <div class="sub-text" style="font-style: italic;">Ref/Notes: ${notes}</div>
          ` : ''}

          <div class="double-divider"></div>
          <div class="row" style="margin-top: 10px; font-size: 0.75rem;">
            <span>${partyType === 'Supplier' ? 'Paid By:' : 'Received By:'} ${cashier || 'Cashier'}</span>
            <span>${partyType === 'Supplier' ? 'Supplier Sign: ________' : 'Customer Sign: ________'}</span>
          </div>

          <div class="center sub-text" style="margin-top: 8px;">*** Thank you! ***</div>
          <div class="cutter-space"></div>
        </div>
      </body>
    </html>
  `;
};

// Generate Payment Voucher Plain Text (ESC/POS)
export const generatePaymentVoucherPlainText = (details) => {
  const {
    voucherNo = '',
    title = 'PAYMENT RECEIPT',
    date = new Date(),
    partyName = 'Customer',
    partyPhone = '',
    partyType = 'Customer',
    invoicesSettled = [],
    paymentMethods = [],
    totalAmount = 0,
    remainingDue = 0,
    storeSettings = {},
    currency = 'Rs.',
    cashier = ''
  } = details || {};

  const storeName = storeSettings?.store_name || 'RESTAURANT';
  const curSymbol = typeof currency === 'object' && currency?.symbol ? currency.symbol : (currency || 'Rs.');
  const fmt = (v) => `${curSymbol} ${Number(v || 0).toFixed(2)}`;

  let txt = "================================\n";
  txt += `<center><b><big>${storeName}</big></b></center>\n`;
  if (storeSettings?.address) txt += `<center>${storeSettings.address}</center>\n`;
  if (storeSettings?.pan_no) txt += `<center>PAN: ${storeSettings.pan_no}</center>\n`;
  txt += "--------------------------------\n";
  txt += `<center><b>${title.toUpperCase()}</b></center>\n`;
  txt += `Voucher: ${voucherNo || '-'}\n`;
  txt += `Date: ${new Date(date).toLocaleString()}\n`;
  txt += `${partyType}: ${partyName} ${partyPhone ? `(${partyPhone})` : ''}\n`;
  txt += "--------------------------------\n";

  if (invoicesSettled && invoicesSettled.length > 0) {
    txt += `${partyType === 'Supplier' ? 'SETTLED BILLS:' : 'SETTLED INVOICES:'}\n`;
    invoicesSettled.forEach(inv => {
      txt += `#${inv.invoice_no || inv.purchase_invoice_no || inv.invoice_id || inv.purchase_id || inv.ref_no}: Cleared ${fmt(inv.allocated || inv.amount)}\n`;
    });
    txt += "--------------------------------\n";
  }

  if (paymentMethods && paymentMethods.length > 0) {
    paymentMethods.forEach(pm => {
      txt += `${pm.method || pm.title}: ${fmt(pm.amount)}\n`;
    });
  } else {
    txt += `Mode: ${details.paymentMethod || 'Cash'}\n`;
  }

  txt += "================================\n";
  txt += `<b>${partyType === 'Supplier' ? 'TOTAL PAID:     ' : 'TOTAL RECEIVED: '} ${fmt(totalAmount)}</b>\n`;
  txt += `<b>REMAINING DUE:  ${fmt(remainingDue)}</b>\n`;
  txt += "================================\n";
  txt += `${partyType === 'Supplier' ? 'Paid By:' : 'Received By:'} ${cashier || 'Cashier'}\n`;
  txt += "<center>*** Thank you! ***</center>\n\n\n";
  return txt;
};

// Universal Payment Voucher Print function
export const printPaymentVoucher = async (details) => {
  const isElectron = isElectronEnv();
  const electron = getElectron();
  const isBluetooth = !!connectedBluetoothDevice;
  const billPrinter = localStorage.getItem("billPrinter") || "Default";
  const pageFormat = details.pageFormat || localStorage.getItem('page_format') || '58';

  const html = generatePaymentVoucherHTML(details, pageFormat);
  const plainText = generatePaymentVoucherPlainText(details);

  if (isBluetooth) {
    try {
      await printViaWebBluetooth(html, plainText);
      return;
    } catch (btErr) {
      console.warn("Bluetooth voucher print failed:", btErr);
    }
  }

  if (isElectron) {
    try {
      if (electron && electron.ipcRenderer) {
        electron.ipcRenderer.send('print-receipt', {
          printerName: billPrinter,
          html,
          plainText,
          copies: 1,
          pageFormat
        });
        return;
      } else if (window.require) {
        window.require('electron').ipcRenderer.send('print-receipt', {
          printerName: billPrinter,
          html,
          plainText,
          copies: 1,
          pageFormat
        });
        return;
      }
    } catch (elErr) {
      console.warn("Electron silent voucher print failed, falling back to agent:", elErr);
    }
  }

  // Web Browser fallback
  try {
    await printViaWebSocketAgent(billPrinter, html, plainText);
  } catch (wsErr) {
    console.warn("WebSocket agent unavailable, using iframe print for voucher:", wsErr);
    const iframe = document.getElementById('voucher-print-iframe') || document.createElement('iframe');
    iframe.id = 'voucher-print-iframe';
    iframe.style.position = 'fixed';
    iframe.style.left = '-9999px';
    iframe.style.width = '300px';
    iframe.style.height = '600px';
    document.body.appendChild(iframe);
    
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();
    setTimeout(() => {
      iframe.contentWindow.print();
    }, 500);
  }
};


